
import React, { useState, useMemo, useEffect, useRef } from "react";
import "./App.css";
import TagSearch from "./TagSearch";
import menuData_en from "./menuData_en";
import { supabase } from './supabase';
import { FaBookmark, FaRegBookmark } from "react-icons/fa";
import Modal from "./components/Modal";
import SubscriptionModal from "./components/SubscriptionModal";
import Header from "./components/Header";
import { slugToKey as sharedSlugToKey } from "./chefConfig";
import useDarkMode from "./hooks/useDarkMode";
import useLanguage from "./hooks/useLanguage";
import { notifyBookmarksChanged } from "./hooks/useSavedCount";
import AnalyzePanel from "./components/AnalyzePanel";
import ChefAI from "./components/ChefAI";
import channelProfiles from "./channelData";
import PaywallModal from './components/PaywallModal';
import translations from "./i18n";
import chefConfig from "./chefConfig";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { track, identifyUser, resetUser } from './analytics';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

const ING_QUANTITY_RE = /^(.+?)\s+(\d[\d/.]*\s*(?:봉지|숟가락|작은술|큰술|티스푼|스푼|그램|밀리리터|밀리|미리|덩어리|움큼|꼬집|방울|가닥|줄기|묶음|뭉치|조각|토막|포기|줌|컵|봉|팩|병|캔|장|마리|알|통|쪽|인분|뿌리|대|근|모|판|ml|ML|kg|KG|mg|개|g|G|L|l|cc|T|t)|약간|조금|조금씩|적당량|적당히|한줌|두줌|한꼬집|두꼬집|반컵|반개|조금)$/;
const parseIngText = (str) => {
  const m = str.trim().match(ING_QUANTITY_RE);
  return m ? { name: m[1].trim(), amount: m[2].trim() } : { name: str.trim(), amount: '' };
};

let ALL_INGREDIENT_NAMES = [];

function SortableIngRow({ id, ing, index, darkMode, editingIngIndex, editingIngName, editingIngAmount, setEditingIngName, setEditingIngAmount, setEditingIngIndex, saveEditIng, startEditIng, setIngredientsList, parseIngredientInput, isRestored, onClearRestored }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging: isSortableDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isSortableDragging ? 0.5 : 1,
  };
  const parsed = parseIngredientInput(ing);
  if (editingIngIndex === index) {
    return (
      <div ref={setNodeRef} style={style} className={`ing-list-row ing-list-row--editing${darkMode ? ' dark' : ''}`}>
        <input
          className={`ing-edit-name${darkMode ? ' dark' : ''}`}
          value={editingIngName}
          onChange={e => { setEditingIngName(e.target.value); onClearRestored?.(); }}
          onKeyDown={e => { if (e.key === 'Enter') saveEditIng(); if (e.key === 'Escape') setEditingIngIndex(null); }}
          autoFocus
        />
        <input
          className={`ing-edit-amount${darkMode ? ' dark' : ''}`}
          value={editingIngAmount}
          onChange={e => setEditingIngAmount(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') saveEditIng(); if (e.key === 'Escape') setEditingIngIndex(null); }}
          placeholder="수량"
        />
        <div className="ing-edit-actions">
          <button className="ing-edit-save" onClick={saveEditIng}>✓</button>
          <button className="ing-edit-cancel" onClick={() => setEditingIngIndex(null)}>✕</button>
        </div>
      </div>
    );
  }
  return (
    <div ref={setNodeRef} style={style} className={`ing-list-row${isRestored ? ' restored' : ''}${darkMode ? ' dark' : ''}`} {...listeners} {...attributes}>
      <span className="ing-drag-handle">⠿</span>
      <div className="ing-list-main">
        <span className="ing-list-name">{parsed.name || ing}</span>
        {parsed.amount && <span className="ing-list-amount">{parsed.amount}</span>}
        <div className="ing-list-actions">
          <button className="ing-list-edit" onPointerDown={e => e.stopPropagation()} onClick={() => startEditIng(index, ing)}>✎</button>
          <button className="ing-list-remove" onPointerDown={e => e.stopPropagation()} onClick={() => setIngredientsList(l => l.filter((_, idx) => idx !== index))}>×</button>
        </div>
      </div>
    </div>
  );
}

// ── 편집 패널: 자체 state로 관리해 App 리렌더 방지 ──
function RecipeEditPanel({ initialDraft, darkMode, t, thumbnailUrl, recipeUrl, uploaderName, onSave, onCancel, onSaveThumbnail, onClearThumbnail }) {
  const [draft, setDraft] = useState(initialDraft);
  const [thumbPreview, setThumbPreview] = useState(thumbnailUrl || null);
  const [thumbUploading, setThumbUploading] = useState(false);
  const [thumbError, setThumbError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [ingredientsList, setIngredientsList] = useState(() =>
    initialDraft.mainIngredients ? initialDraft.mainIngredients.split('\n').filter(Boolean) : []
  );
  const [ingInput, setIngInput] = useState('');
  const [ingAmountInput, setIngAmountInput] = useState('');
  const [ingComposing, setIngComposing] = useState(false);
  const [stepComposing, setStepComposing] = useState(false);
  const nameInputRef = useRef(null);
  const [editingIngIndex, setEditingIngIndex] = useState(null);
  const [editingIngName, setEditingIngName] = useState('');
  const [editingIngAmount, setEditingIngAmount] = useState('');

  // autocomplete state
  const [ingSuggestions, setIingSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggIdx, setActiveSuggIdx] = useState(-1);

  // restored item highlight — stored as Set<string> (item values) so drag reorder doesn't break it
  const [restoredItems, setRestoredItems] = useState(null); // null | Set<string>
  const [restoredStepItems, setRestoredStepItems] = useState(null); // null | Set<string>

  // draft restore banner state
  const [draftBanner, setDraftBanner] = useState(null); // null | { draft, ingredientsList, stepsList, savedAt }

  const draftKey = `findish_draft_${recipeUrl}`;
  const autoSaveTimer = useRef(null);

  const parseIngredientInput = parseIngText;

  const startEditIng = (i, ing) => {
    const parsed = parseIngredientInput(ing);
    setEditingIngIndex(i);
    setEditingIngName(parsed.name || ing);
    setEditingIngAmount(parsed.amount || '');
  };

  const saveEditIng = () => {
    if (!editingIngName.trim()) return;
    const combined = editingIngAmount.trim()
      ? `${editingIngName.trim()} ${editingIngAmount.trim()}`
      : editingIngName.trim();
    setIngredientsList(l => l.map((item, idx) => idx === editingIngIndex ? combined : item));
    setEditingIngIndex(null);
  };

  const [stepsList, setStepsList] = useState(() =>
    initialDraft.steps ? initialDraft.steps.split('\n').filter(Boolean) : ['']
  );
  const tipLabel = uploaderName ? `${uploaderName}'s TIP` : t.tip;

  const [ingParsing, setIngParsing] = useState(false);

  // Check localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(draftKey);
    if (saved) {
      try { setDraftBanner(JSON.parse(saved)); } catch {}
    }
    // beforeunload warning
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save with 500ms debounce
  useEffect(() => {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      localStorage.setItem(draftKey, JSON.stringify({ draft, ingredientsList, stepsList, savedAt: Date.now() }));
    }, 500);
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, ingredientsList, stepsList]);

  const addIngredient = async (val) => {
    const rawName = (val ?? ingInput).trim();
    const rawAmount = ingAmountInput.trim();
    if (!rawName) return;
    let name = rawName;
    let amount = rawAmount;
    if (rawName && !rawAmount) {
      try {
        setIngParsing(true);
        const res = await fetch(`${API_BASE}/parse-ingredient`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: rawName }),
        });
        const data = await res.json();
        name = data.name || rawName;
        amount = data.amount || '';
      } catch {
        const parsed = parseIngredientInput(rawName);
        name = parsed.name;
        amount = parsed.amount;
      } finally {
        setIngParsing(false);
      }
    }
    const combined = amount ? `${name} ${amount}` : name;
    setIngredientsList(l => [...l, combined]);
    setIngInput('');
    setIngAmountInput('');
    setShowSuggestions(false);
    setIingSuggestions([]);
    setActiveSuggIdx(-1);
    setTimeout(() => nameInputRef.current?.focus(), 0);
  };

  const handleThumbFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { setThumbError('이미지 파일만 업로드 가능합니다.'); return; }
    if (file.size > 5 * 1024 * 1024) { setThumbError('5MB 이하 파일만 업로드 가능합니다.'); return; }
    setThumbError('');
    setThumbUploading(true);
    const localPreview = URL.createObjectURL(file);
    setThumbPreview(localPreview);
    try {
      const ytId = recipeUrl?.match(/(?:v=|youtu\.be\/)([^&?/]+)/)?.[1] || Date.now();
      const formData = new FormData();
      formData.append('file', file);
      formData.append('key', `thumbnails/${ytId}_${Date.now()}`);
      const uploadRes = await fetch(`${API_BASE}/upload-thumbnail`, { method: 'POST', body: formData });
      const uploadData = await uploadRes.json();
      if (!uploadData.ok) throw new Error(uploadData.error || 'upload failed');
      onSaveThumbnail(uploadData.url);
      setThumbPreview(uploadData.url);
    } catch (e) {
      setThumbError('업로드 실패. 다시 시도해주세요.');
      setThumbPreview(thumbnailUrl || null);
    } finally {
      setThumbUploading(false);
    }
  };

  const makeStepHandlers = (list, setList, prefix) => ({
    onChange: (i, val) => { const n = [...list]; n[i] = val; setList(n); },
    onKeyDown: (e, i) => {
      if (e.key === 'Enter' && !stepComposing) {
        e.preventDefault();
        const n = [...list]; n.splice(i + 1, 0, ''); setList(n);
        setTimeout(() => document.getElementById(`${prefix}-${i + 1}`)?.focus(), 0);
      }
      if (e.key === 'Backspace' && list[i] === '' && list.length > 1) {
        e.preventDefault();
        setList(list.filter((_, idx) => idx !== i));
        setTimeout(() => document.getElementById(`${prefix}-${Math.max(0, i - 1)}`)?.focus(), 0);
      }
    },
    onRemove: (i) => setList(list.filter((_, idx) => idx !== i)),
    onAdd: () => setList([...list, '']),
  });

  const stepHandlers = makeStepHandlers(stepsList, setStepsList, 'step-input');

  const handleSave = () => {
    localStorage.removeItem(draftKey);
    onSave({ ...draft, mainIngredients: ingredientsList.filter(Boolean).join('\n'), steps: stepsList.filter(Boolean).join('\n') });
  };

  const handleCancel = () => {
    const saved = localStorage.getItem(draftKey);
    if (saved) {
      if (window.confirm('작업 중인 내용이 있습니다. 취소하시겠습니까?')) {
        localStorage.removeItem(draftKey);
        onCancel();
      }
    } else {
      onCancel();
    }
  };

  return (
    <div className="recipe-edit-panel">
      <div className={`recipe-edit-topbar${darkMode ? ' dark' : ''}`}>
        <span className="recipe-edit-topbar-title">편집 중</span>
        <div className="recipe-edit-topbar-actions">
          <button className={`recipe-edit-cancel-btn${darkMode ? ' dark' : ''}`} onClick={handleCancel}>취소</button>
          <button className={`recipe-edit-save-btn${darkMode ? ' dark' : ''}`} onClick={handleSave}>저장</button>
        </div>
      </div>
      {draftBanner && (
        <div className={`draft-restore-banner${darkMode ? ' dark' : ''}`}>
          <span>
            <strong>"{draftBanner.draft?.name || '이전 작업'}"</strong> 임시 저장된 내용이 있어요.
          </span>
          <div>
            <button className="draft-restore-btn" onClick={() => {
              if (draftBanner.draft) setDraft(draftBanner.draft);
              if (draftBanner.ingredientsList) {
                const currentSet = new Set(ingredientsList.filter(Boolean));
                const diffValues = new Set(
                  draftBanner.ingredientsList.filter(item => item && !currentSet.has(item))
                );
                setIngredientsList(draftBanner.ingredientsList);
                setRestoredItems(diffValues.size > 0 ? diffValues : null);
              }
              if (draftBanner.stepsList) {
                const currentStepSet = new Set(stepsList.filter(Boolean));
                const diffStepValues = new Set(
                  draftBanner.stepsList.filter(item => item && !currentStepSet.has(item))
                );
                setStepsList(draftBanner.stepsList);
                setRestoredStepItems(diffStepValues.size > 0 ? diffStepValues : null);
              }
              setDraftBanner(null);
            }}>복원</button>
            <button className="draft-dismiss-btn" onClick={() => {
              localStorage.removeItem(draftKey);
              setDraftBanner(null);
            }}>무시</button>
          </div>
        </div>
      )}
      <label className="recipe-edit-field-label">요리명</label>
      <input
        className={`recipe-edit-input${darkMode ? ' dark' : ''}`}
        value={draft.name || ''}
        onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
        placeholder="요리 이름"
      />
      <label className="recipe-edit-field-label" style={{ marginTop: 12 }}>
        👨‍🍳 {tipLabel} <span style={{ fontWeight: 400, opacity: 0.5 }}>(선택)</span>
      </label>
      <textarea
        className={`recipe-edit-textarea${darkMode ? ' dark' : ''}`}
        value={draft.tip}
        onChange={e => setDraft(d => ({ ...d, tip: e.target.value }))}
        rows={3}
        placeholder="재료 준비 팁이나 맛을 더하는 포인트를 남겨보세요"
      />
      <div className="recipe-edit-recipe-header" style={{ marginTop: 12 }}>
        <label className="recipe-edit-field-label" style={{ margin: 0 }}>RECIPE</label>
        <input
          className={`recipe-servings-input${darkMode ? ' dark' : ''}`}
          value={draft.servings || ''}
          onChange={e => setDraft(d => ({ ...d, servings: e.target.value }))}
          placeholder="인분"
        />
      </div>
      <div className="ing-list-outer">
        <div className={`ing-list-container${darkMode ? ' dark' : ''}`}>
          {ingredientsList.length > 0 && (
            <div className={`ing-list-header${darkMode ? ' dark' : ''}`}>
              <span>재료</span>
              <span>수량</span>
              <span />
            </div>
          )}
          <DndContext collisionDetection={closestCenter} onDragEnd={({ active, over }) => {
            if (!over || active.id === over.id) return;
            const oldIndex = parseInt(active.id, 10);
            const newIndex = parseInt(over.id, 10);
            setIngredientsList(l => arrayMove(l, oldIndex, newIndex));
          }}>
            <SortableContext items={ingredientsList.map((_, i) => String(i))} strategy={verticalListSortingStrategy}>
              {ingredientsList.map((ing, i) => (
                <SortableIngRow
                  key={String(i)}
                  id={String(i)}
                  ing={ing}
                  index={i}
                  darkMode={darkMode}
                  editingIngIndex={editingIngIndex}
                  editingIngName={editingIngName}
                  editingIngAmount={editingIngAmount}
                  setEditingIngName={setEditingIngName}
                  setEditingIngAmount={setEditingIngAmount}
                  setEditingIngIndex={setEditingIngIndex}
                  saveEditIng={saveEditIng}
                  startEditIng={startEditIng}
                  setIngredientsList={setIngredientsList}
                  parseIngredientInput={parseIngredientInput}
                  isRestored={restoredItems?.has(ing) ?? false}
                  onClearRestored={() => setRestoredItems(prev => { if (!prev) return prev; const next = new Set(prev); next.delete(ing); return next.size ? next : null; })}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
        <div className={`ing-add-row${darkMode ? ' dark' : ''}`}>
          <div className="ing-autocomplete-wrap">
            <input
              ref={nameInputRef}
              className={`ing-add-name${darkMode ? ' dark' : ''}`}
              value={ingInput}
              onChange={e => {
                const val = e.target.value;
                setIngInput(val);
                if (val.trim()) {
                  const filtered = ALL_INGREDIENT_NAMES.filter(n => n.includes(val.trim())).slice(0, 8);
                  setIingSuggestions(filtered);
                  setShowSuggestions(filtered.length > 0);
                  setActiveSuggIdx(-1);
                } else {
                  setShowSuggestions(false);
                  setIingSuggestions([]);
                }
              }}
              onCompositionStart={() => setIngComposing(true)}
              onCompositionEnd={e => { setIngComposing(false); setIngInput(e.target.value); }}
              onKeyDown={e => {
                if (showSuggestions && ingSuggestions.length > 0) {
                  if (e.key === 'ArrowDown') { e.preventDefault(); setActiveSuggIdx(i => Math.min(i + 1, ingSuggestions.length - 1)); return; }
                  if (e.key === 'ArrowUp') { e.preventDefault(); setActiveSuggIdx(i => Math.max(i - 1, -1)); return; }
                  if (e.key === 'Escape') { setShowSuggestions(false); return; }
                  if (e.key === 'Enter' && !ingComposing) {
                    e.preventDefault();
                    if (activeSuggIdx >= 0) {
                      setIngInput(ingSuggestions[activeSuggIdx] + ' ');
                      setShowSuggestions(false);
                      setActiveSuggIdx(-1);
                    } else {
                      setShowSuggestions(false);
                      setActiveSuggIdx(-1);
                    }
                    return;
                  }
                } else {
                  if (e.key === 'Enter' && !ingComposing) {
                    e.preventDefault();
                    if (!ingAmountInput.trim()) {
                      const parsed = parseIngredientInput(ingInput.trim());
                      if (parsed.amount) {
                        setIngInput(parsed.name);
                        setIngAmountInput(parsed.amount);
                        setTimeout(() => document.getElementById('ing-amount-input')?.focus(), 0);
                        return;
                      }
                    }
                    addIngredient();
                  }
                }
              }}
              onBlur={() => { setTimeout(() => setShowSuggestions(false), 150); }}
              placeholder="재료명 (예: 양파 5개)"
            />
            {showSuggestions && ingSuggestions.length > 0 && (
              <div className={`ing-suggestions${darkMode ? ' dark' : ''}`}>
                {ingSuggestions.map((s, idx) => (
                  <div
                    key={s}
                    className={`ing-suggestion-item${idx === activeSuggIdx ? ' active' : ''}`}
                    onMouseDown={() => {
                      setIngInput(s + ' ');
                      setShowSuggestions(false);
                      setActiveSuggIdx(-1);
                      setTimeout(() => nameInputRef.current?.focus(), 0);
                    }}
                  >{s}</div>
                ))}
              </div>
            )}
          </div>
          <input
            id="ing-amount-input"
            className={`ing-add-amount${darkMode ? ' dark' : ''}`}
            value={ingAmountInput}
            onChange={e => setIngAmountInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); addIngredient(); }
            }}
            placeholder="수량"
          />
          <button className="ing-add-btn" onClick={() => addIngredient()} disabled={ingParsing}>
            {ingParsing ? '...' : '추가'}
          </button>
        </div>
      </div>{/* ing-list-outer */}
      <label className="recipe-edit-field-label" style={{ marginTop: 12 }}>INSTRUCTION</label>
      <div className="steps-edit-list">
        {stepsList.map((step, i) => (
          <div key={i} className={`step-edit-row${restoredStepItems?.has(step) ? ' restored' : ''}`}>
            <span className="step-edit-number">{i + 1}.</span>
            <input
              id={`step-input-${i}`}
              className={`step-edit-input${darkMode ? ' dark' : ''}`}
              value={step}
              onChange={e => {
                stepHandlers.onChange(i, e.target.value);
                setRestoredStepItems(prev => { if (!prev) return prev; const next = new Set(prev); next.delete(step); return next.size ? next : null; });
              }}
              onCompositionStart={() => setStepComposing(true)}
              onCompositionEnd={() => setStepComposing(false)}
              onKeyDown={e => stepHandlers.onKeyDown(e, i)}
              placeholder={`스텝 ${i + 1}`}
            />
            {stepsList.length > 1 && (
              <button className="step-edit-remove" onClick={() => stepHandlers.onRemove(i)}>×</button>
            )}
          </div>
        ))}
        <button className="step-edit-add" onClick={stepHandlers.onAdd}>+ 스텝 추가</button>
      </div>

      <label className="recipe-edit-field-label" style={{ marginTop: 12 }}>
        썸네일 <span style={{ fontWeight: 400, opacity: 0.5 }}>(선택)</span>
      </label>
      <div
        className={`thumb-upload-zone${isDragging ? ' dragging' : ''}${darkMode ? ' dark' : ''}`}
        onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={e => { e.preventDefault(); setIsDragging(false); handleThumbFile(e.dataTransfer.files[0]); }}
        onClick={() => document.getElementById('thumb-file-input').click()}
      >
        {thumbPreview ? (
          <img src={thumbPreview} alt="썸네일 미리보기" className="thumb-preview-img" />
        ) : (
          <div className="thumb-upload-placeholder">
            <span>사진을 드래그하거나 클릭해서 업로드</span>
            <span className="thumb-upload-sub">JPG, PNG, WEBP · 최대 5MB</span>
          </div>
        )}
        {thumbUploading && <div className="thumb-uploading-overlay">업로드 중...</div>}
      </div>
      <input id="thumb-file-input" type="file" accept="image/*" style={{ display: 'none' }}
        onChange={e => handleThumbFile(e.target.files[0])} />
      {thumbError && <p className="thumb-error-msg">{thumbError}</p>}
      {thumbnailUrl && (
        <button className="thumb-revert-btn"
          onClick={() => { setThumbPreview(null); onClearThumbnail(); }}>
          원본으로 되돌리기
        </button>
      )}

    </div>
  );
}

// ── 로그인 모달 ──
function LoginModal({ open, onClose, onLoginSuccess, darkMode }) {
  const [showCreatorForm, setShowCreatorForm] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [kakaoLoading, setKakaoLoading] = useState(false);

  useEffect(() => {
    if (!open) setShowCreatorForm(false);
  }, [open]);

  const handleClose = () => {
    onClose();
    setError('');
    setUsername('');
    setPassword('');
    setShowCreatorForm(false);
    setKakaoLoading(false);
  };

  const handleKakaoLogin = async () => {
    setKakaoLoading(true);
    track("signup_started", { provider: "kakao" });
    await supabase.auth.signInWithOAuth({
      provider: 'kakao',
      options: {
        redirectTo: window.location.origin,
        scopes: 'profile_nickname profile_image',
      },
    });
  };

  const handleCreatorLogin = async () => {
    if (!username.trim() || !password) return;
    setError('');
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: `${username.trim()}@findish.internal`,
        password,
      });
      if (error) { setError('아이디 또는 비밀번호가 틀렸습니다.'); return; }
      await onLoginSuccess(data.session);
      setUsername('');
      setPassword('');
    } catch {
      setError('로그인에 실패했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} darkMode={darkMode}>
      <div className="login-modal">
        {!showCreatorForm ? (
          <>
            <h3 className="login-modal-title">로그인하고 레시피 저장하기!</h3>
            <div className="login-social-group">
              <button
                onClick={handleKakaoLogin}
                className="social-login-btn kakao-btn"
                disabled={kakaoLoading}
                style={{ fontSize: '0.95rem', padding: '13px 12px' }}
              >
                {kakaoLoading ? (
                  '카카오 페이지로 이동 중...'
                ) : (
                  <>
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M9 1.5C4.86 1.5 1.5 4.19 1.5 7.5c0 2.1 1.23 3.94 3.09 5.04L3.75 15l3.4-1.78A8.7 8.7 0 009 13.5c4.14 0 7.5-2.69 7.5-6S13.14 1.5 9 1.5z" fill="#191919"/></svg>
                    카카오로 1초 만에 시작하기
                  </>
                )}
              </button>
            </div>
            <button
              className="creator-login-toggle"
              onClick={() => setShowCreatorForm(true)}
            >
              크리에이터이신가요? 로그인 →
            </button>
          </>
        ) : (
          <>
            <h3 className="login-modal-title">크리에이터 로그인</h3>
            <div className="login-field">
              <label className="login-label">아이디</label>
              <input
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCreatorLogin()}
                placeholder="크리에이터 아이디"
                className={`login-input${darkMode ? ' dark' : ''}`}
                autoComplete="username"
              />
            </div>
            <div className="login-field">
              <label className="login-label">비밀번호</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCreatorLogin()}
                placeholder="비밀번호"
                className={`login-input${darkMode ? ' dark' : ''}`}
                autoComplete="current-password"
              />
            </div>
            {error && <p className="login-error">{error}</p>}
            <button onClick={handleCreatorLogin} className="login-submit-btn" disabled={loading}>
              {loading ? '로그인 중...' : '로그인'}
            </button>
            <button
              className="creator-login-toggle"
              onClick={() => { setShowCreatorForm(false); setError(''); }}
            >
              ← 돌아가기
            </button>
          </>
        )}
      </div>
    </Modal>
  );
}

// slug → chefKey 역방향 맵 (chefConfig에서 재수출)
const slugToKey = sharedSlugToKey;

function extractYouTubeId(url) {
  const match = url.match(/(?:\?v=|\/embed\/|\.be\/|\/v\/|\/shorts\/)([A-Za-z0-9_-]{11})/);
  return match ? match[1] : null;
}

const RecipeCard = React.memo(function RecipeCard({
  item, thumbnailSrc, isHidden, isSaved, isMyRecipe, isCreator, isAdmin, isDuplicate,
  selectedIngredientValues, selectedIngredients, language,
  normalizeIng, style, onTagClick, onChefClick,
  onOpen, onToggleSave, onToggleHidden, onEdit, onDelete, onCopyPromoLink,
}) {
  const allNormalized = [...new Set((item.ingredients || []).map(ing => normalizeIng(ing)))];
  const mainIngs = allNormalized.filter(ing => !SEASONINGS.has(ing));
  const seasoningIngs = allNormalized.filter(ing => SEASONINGS.has(ing));
  const highlightedMain = mainIngs.filter(ing => selectedIngredientValues.has(ing.toLowerCase()));
  const restMain = mainIngs.filter(ing => !selectedIngredientValues.has(ing.toLowerCase()));
  const cardIngredients = [...highlightedMain, ...restMain];
  const matchCount = mainIngs.filter(ing => {
    const normalizedIng = normalizeIng(ing);
    return selectedIngredients.filter(s => s.group !== "menu").some(opt => {
      const normalizedVal = normalizeIng(opt.value);
      if (parentMap[normalizedVal]) {
        const allVariants = [normalizedVal, ...parentMap[normalizedVal].map(c => normalizeIng(c))];
        return allVariants.includes(normalizedIng);
      }
      return normalizedIng.toLowerCase() === normalizedVal.toLowerCase();
    });
  }).length;

  return (
    <li className={`menu-card${isHidden ? ' card-hidden' : ''}`} style={style} onClick={onOpen}>
      <div className="menu-thumbnail-wrap">
        {thumbnailSrc && (
          <img src={thumbnailSrc} alt={item.name} className={`menu-thumbnail${isHidden ? ' thumbnail-hidden' : ''}`} loading="lazy" decoding="async" />
        )}
        {item.created_at && (Date.now() - new Date(item.created_at).getTime() < 36 * 60 * 60 * 1000) && (
          <span className="menu-card-new-badge">NEW</span>
        )}
        {isHidden && (
          <span className="hidden-overlay-label">비공개</span>
        )}
        {!isHidden && (() => {
          const tags = item.tags || [];
          if (tags.length === 0) return null;
          return (
            <div className="menu-card-tags">
              {tags.map((tag, i) => (
                <span key={i} className="menu-card-tag-badge" onClick={e => { e.stopPropagation(); onTagClick && onTagClick(tag); }}>{tag}</span>
              ))}
            </div>
          );
        })()}
      </div>
      {isDuplicate && (
        <span className="menu-duplicate-badge">중복</span>
      )}
      {(isMyRecipe || (isAdmin && onCopyPromoLink)) ? (
        <div className="menu-card-actions">
          {onCopyPromoLink && (
            <button
              className="menu-card-action-btn"
              onClick={onCopyPromoLink}
              title={isMyRecipe ? "내 홍보 링크 복사 (?ref 자동 삽입)" : "크리에이터 홍보 링크 복사 (?ref 자동 삽입)"}
            >
              링크
            </button>
          )}
          {isMyRecipe && (
            <>
              <button className="menu-card-action-btn" onClick={onToggleHidden}>
                {isHidden ? '비공개' : '공개'}
              </button>
              <button className="menu-card-action-btn" onClick={onEdit}>편집</button>
            </>
          )}
          <button
            className={`menu-card-action-btn${isSaved ? " saved" : ""}`}
            onClick={onToggleSave}
            title={isSaved ? "저장 취소" : "저장하기"}
          >
            {isSaved ? <FaBookmark size={12} /> : <FaRegBookmark size={12} />}
          </button>
        </div>
      ) : (
        <button
          className={`menu-card-save-btn${isSaved ? " saved" : ""}`}
          onClick={onToggleSave}
          title={isSaved ? (language === "kr" ? "저장 취소" : "Unsave") : (language === "kr" ? "저장하기" : "Save")}
        >
          {isSaved ? <FaBookmark size={14} /> : <FaRegBookmark size={14} />}
        </button>
      )}
      <div className="menu-text">
        <div className="menu-name">{item.name || "No Name"}</div>
        {item.uploader && (
          <div className="menu-chef-row">
            <img
              src={channelProfiles[item.uploader] || ""}
              alt={item.uploader}
              className="menu-chef-avatar"
              onError={(e) => { e.target.style.display = "none"; }}
              onClick={e => { e.stopPropagation(); onChefClick && onChefClick(item.uploader); }}
              style={{ cursor: onChefClick ? "pointer" : "default" }}
            />
            <span
              className="menu-uploader"
              onClick={e => { e.stopPropagation(); onChefClick && onChefClick(item.uploader); }}
              style={{ cursor: onChefClick ? "pointer" : "default" }}
            >{item.uploader}</span>
            {matchCount > 0 && (
              <span className="menu-match-badge">{matchCount}/{mainIngs.length}</span>
            )}
          </div>
        )}
        <div className="ingredient-tags">
          {cardIngredients.slice(0, 5).map((ing, i) => {
            const isHighlighted = selectedIngredientValues.has(ing.toLowerCase());
            const displayName = parseIngText(ing).name;
            return (
              <span key={i} className="pill-tip-wrapper" data-tip={ing}>
                <span className={`ingredient-pill${isHighlighted ? " ingredient-pill-highlight" : ""}`}>
                  {displayName}
                </span>
              </span>
            );
          })}
          {cardIngredients.length > 5 && (
            <span className="ingredient-pill ingredient-pill-more">
              +{cardIngredients.length - 5}
            </span>
          )}
          {seasoningIngs.length > 0 && (
            <span className="ingredient-pill ingredient-pill--seasoning ingredient-pill--seasoning-card">
              {language === "kr" ? `+양념 ${seasoningIngs.length}` : `+Seasonings ${seasoningIngs.length}`}
            </span>
          )}
        </div>
      </div>
    </li>
  );
}, (prev, next) => (
  prev.item === next.item &&
  prev.thumbnailSrc === next.thumbnailSrc &&
  prev.isHidden === next.isHidden &&
  prev.isSaved === next.isSaved &&
  prev.isMyRecipe === next.isMyRecipe &&
  prev.isCreator === next.isCreator &&
  prev.isAdmin === next.isAdmin &&
  prev.isDuplicate === next.isDuplicate &&
  prev.selectedIngredientValues === next.selectedIngredientValues &&
  prev.selectedIngredients === next.selectedIngredients &&
  prev.language === next.language &&
  prev.normalizeIng === next.normalizeIng &&
  prev.style === next.style
));

const EN_PLURAL_MAP = {
  "eggs": "egg", "egg yolks": "egg yolk",
  "tomatoes": "tomato", "onions": "onion", "green onions": "green onion",
  "mushrooms": "mushroom", "potatoes": "potato", "carrots": "carrot",
  "cloves": "clove", "anchovies": "anchovy", "olives": "olive",
  "capers": "caper", "lemons": "lemon", "limes": "lime",
  "shallots": "shallot", "scallions": "scallion", "peppers": "pepper",
  "bay leaves": "bay leaf", "herbs": "herb", "oysters": "oyster",
};

// 의미상 동일한 영어 재료 통합 맵
const EN_SYNONYM_MAP = {
  // 마늘
  "minced garlic": "garlic", "garlic clove": "garlic",
  // 양파
  "red onion": "onion",
  // 올리브 오일
  "extra virgin olive oil": "olive oil", "pure olive oil": "olive oil",
  // 파르메산
  "parmigiano reggiano cheese": "parmesan cheese",
  "parmigiano-reggiano cheese": "parmesan cheese",
  "reggiano cheese": "parmesan cheese",
  "pecorino romano cheese": "pecorino cheese",
  // 치즈 표기 통일
  "mozzarella": "mozzarella cheese",
  "mascarpone": "mascarpone cheese",
  "ricotta": "ricotta cheese",
  // 파
  "scallion": "green onion",
  // 크림
  "whipping cream": "heavy cream",
  // 토마토 (가공품은 원재료로 통일)
  "whole peeled tomatoes": "tomato", "whole tomatoes": "tomato", "canned tomatoes": "tomato",
  // 소스
  "tabasco sauce": "tabasco", "sriracha sauce": "sriracha",
  // 빵
  "bread slices": "bread",
  // 파스타 → pasta로 통합
  "spaghetti": "pasta", "linguine": "pasta", "spaghettini": "pasta",
  "rigatoni": "pasta", "orecchiette": "pasta", "penne pasta": "pasta", "tagliatelle": "pasta",
  // 올리브 종류 → olive
  "black olives": "olive", "green olives": "olive", "taggiasca olives": "olive",
  // 고추 표기 통일
  "cheongyang chili": "cheongyang chili pepper", "cheongyang pepper": "cheongyang chili pepper",
  "red chili": "red chili pepper",
  "green chili": "green chili pepper",
  "red bell pepper": "bell pepper",
  // 깨
  "black sesame seeds": "sesame seeds",
  // 문어
  "webfoot octopus": "octopus",
};

const isValidRecipe = (item) =>
  item.name !== "Only 제품 설명 OR 홍보" &&
  item.name !== "건너뜀 - 영상 너무 김" &&
  item.name !== "분석 불가" &&
  !(item.ingredients || []).includes("Only 제품 설명 OR 홍보");

const ADMIN_UID = 'c2163866-5b15-4e2a-a2db-61b15771646e';

const SEASONINGS = new Set([
  "소금","후추","설탕","밀가루","기름","면수","샐러리 소금","백후추","핑크 페퍼콘","슈가파우더","아이싱 슈가",
  "올리브 오일","식용유","참기름","들기름","고추기름","오리 기름","트러플","트러플 오일","화이트 트러플 오일","오일",
  "간장","된장","고추장","고춧가루","참치액","연두","새우젓","액젓",
  "청주","미림","유자청","청하",
  "식초","발사믹 식초","화이트 발사믹","사과 식초","와인 식초","레드와인 식초","화이트 와인 비니거",
  "꿀","조청","물엿","올리고당","매실청","매실액",
  "케첩","마요네즈","굴소스","굴 소스","우스터 소스","타바스코","타바스코 소스","스리라차 소스","디종 머스타드","홀그레인 머스타드",
  "치킨스톡","치킨 스톡","코인 육수","야채 육수","채소 육수","조개 육수","해물 육수",
  "월계수잎","타임","로즈마리","오레가노","차이브","민트","파슬리","허브","양꼬치 시즈닝",
  "깨","흰깨","검은깨","통깨",
  "파프리카 파우더","파프리카 가루","스모크 파프리카","넛맥","계피","클로브",
  "전분","베이킹 파우더","MSG","옥수수 전분","클래식 비네그레트",
  "salt","pepper","sugar","flour","oil",
  "olive oil","sesame oil","vegetable oil","perilla oil","chili oil",
  "truffle","truffle oil","white truffle oil",
  "soy sauce","miso","gochujang","red pepper flakes","fish sauce","salted shrimp",
  "rice wine","mirin","yuja syrup","cheongha","sake",
  "vinegar","balsamic vinegar","balsamic glaze","white balsamic","apple cider vinegar",
  "wine vinegar","red wine vinegar","white wine vinegar",
  "honey","corn syrup","rice syrup","oligosaccharide","plum syrup",
  "ketchup","mayonnaise","oyster sauce","worcestershire sauce","hot sauce",
  "tabasco","sriracha","dijon mustard","whole grain mustard",
  "chicken stock","vegetable stock","seafood stock","chicken broth","vegetable broth","broth","beef stock",
  "bay leaf","thyme","rosemary","oregano","chives","mint","herb",
  "parsley","basil","dill","cilantro",
  "black pepper","white pepper","pink peppercorns","cayenne pepper",
  "peperoncino","chili powder","red pepper powder","gochugaru",
  "paprika powder","smoked paprika","nutmeg","cinnamon","clove",
  "curry powder","celery salt",
  "cornstarch","starch","baking powder","msg",
  "brown sugar","powdered sugar","classic vinaigrette","perilla seed powder",
]);

const parentMap = {
  "돼지고기": ["삼겹살","목살","항정살","돼지등갈비","돼지 뒷다리살","돼지 앞다리살","돼지 등심","돼지갈비","듀록"],
  "닭고기": ["닭가슴살","닭다리","닭다리살","수비드 닭가슴살","닭발","닭뼈","닭간","토종닭 다리"],
  "소고기": ["소고기 갈비살","안심","양갈비","LA갈비","소고기 등심","한우패티","토마호크","채끝살","우둔살","우삼겹","떡갈비"],
  "치즈": ["파마산 치즈","크림치즈","페타 치즈","부라타 치즈","블루 치즈","까망베르 치즈","고르곤졸라 치즈","리코타 치즈","모짜렐라 치즈","그뤼에르 치즈","체다 치즈"],
  "김치": ["묵은지","백김치"],
  "버섯": ["표고 버섯","느타리 버섯","팽이 버섯","새송이 버섯","포르치니 버섯","양송이 버섯"],
  "토마토": ["방울토마토","선드라이토마토","토마토소스","토마토홀"],
  "식초": ["발사믹 식초","화이트 발사믹"],
  "빵": ["식빵"],
  "파스타": ["스파게티","링귀네","펜네","부카티니","카펠리니","탈리아텔레"],
};

function App() {
  const { slug, recipeSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const CHEF_FILTER = slugToKey[slug] || process.env.REACT_APP_CHEF || null;
  const chefProfile = CHEF_FILTER ? chefConfig[CHEF_FILTER] : null;

  const [analyzeOpen, setAnalyzeOpen] = useState(false);
  const [creatorTab, setCreatorTab] = useState('all');
  const [recipeModal, setRecipeModal] = useState(null);
  const [showAllReviews, setShowAllReviews] = useState(false);
  useEffect(() => { setShowAllReviews(false); }, [recipeModal?.id]);
  const [galleryLightbox, setGalleryLightbox] = useState(null); // {src, stepText, index}
  const [galleryGenerating, setGalleryGenerating] = useState({}); // { [url]: bool }
  const [activeTab, setActiveTab] = useState("home"); // "home" | "chef" | "saved"
  const [savedRecipes, setSavedRecipes] = useState([]);
  const [toast, setToast] = useState(null); // { message, key }

  const [language] = useLanguage();
  const t = translations[language];
  const [darkMode] = useDarkMode();
  const [searchActive, setSearchActive] = useState(false);
  const [modalVideoPlaying, setModalVideoPlaying] = useState(false);

  const [creatorUser, setCreatorUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('findish_creator')) || null; } catch { return null; }
  });
  const [socialUser, setSocialUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('findish_social')) || null; } catch { return null; }
  });
  const [socialLoading, setSocialLoading] = useState(true);
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  // Header가 로그아웃하면 localStorage를 지우고 'findish-social-changed' 이벤트를 쏨.
  // App.js의 socialUser/creatorUser 상태도 그에 맞춰 재동기화.
  useEffect(() => {
    const sync = () => {
      try {
        const s = JSON.parse(localStorage.getItem('findish_social')) || null;
        const c = JSON.parse(localStorage.getItem('findish_creator')) || null;
        setSocialUser(s);
        setCreatorUser(c);
      } catch {}
    };
    window.addEventListener('findish-social-changed', sync);
    return () => window.removeEventListener('findish-social-changed', sync);
  }, []);
  const [subscriptionModal, setSubscriptionModal] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null); // null | { status, trial_ends_at, next_billing_at }
  const [thumbnailOverrides, setThumbnailOverrides] = useState(() => {
    try { return JSON.parse(localStorage.getItem('findish_thumbnails')) || {}; } catch { return {}; }
  });
  const [recipeEdits, setRecipeEdits] = useState(() => {
    try { return JSON.parse(localStorage.getItem('findish_recipe_edits')) || {}; } catch { return {}; }
  });
  const [recipeData, setRecipeData] = useState([]);
  const [recipeLoading, setRecipeLoading] = useState(true);

  const CACHE_KEY = 'findish_recipes_kr_v1';
  const applyRecipeData = (data) => {
    setRecipeData(data);
    setRecipeLoading(false);
    ALL_INGREDIENT_NAMES = [...new Set(
      data.flatMap(item => (item.ingredients || []).map(raw => parseIngText(String(raw)).name).filter(Boolean))
    )].sort((a, b) => a.localeCompare(b, 'ko'));
  };

  const startGalleryGeneration = async (recipeId, recipeUrl) => {
    const POLL_KEY = 'findish_gallery_pending';
    const startedAt = Date.now();
    setGalleryGenerating(prev => ({ ...prev, [recipeId]: true }));
    // 현재 step_images 스냅샷 저장 (재생성 시 "이전 것과 달라졌나" 비교용)
    const prevImgKeys = JSON.stringify(
      Object.keys(recipeModal?.step_images || {}).filter(k => k !== 'dish').sort()
    );
    try {
      const pending = JSON.parse(localStorage.getItem(POLL_KEY) || '{}');
      localStorage.setItem(POLL_KEY, JSON.stringify({ ...pending, [recipeId]: startedAt }));
    } catch {}
    try {
      await fetch(`${API_BASE}/generate-gallery`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipe_id: recipeId, url: recipeUrl }),
      });
    } catch {}
    // 폴링: 첫 30초는 기다렸다가(처리 시간) 이후 15초마다 체크
    await new Promise(r => setTimeout(r, 30000));
    const pollInterval = setInterval(async () => {
      try {
        const { data } = await supabase.from('recipes').select('step_images').eq('id', recipeId).single();
        const imgs = data?.step_images || {};
        const newKeys = JSON.stringify(Object.keys(imgs).filter(k => k !== 'dish').sort());
        const hasNew = Object.keys(imgs).filter(k => k !== 'dish').length > 0 && newKeys !== prevImgKeys;
        // 신규 생성이거나 재생성으로 키가 바뀐 경우
        if (hasNew || (Object.keys(imgs).filter(k => k !== 'dish').length > 0 && prevImgKeys === '[]')) {
          clearInterval(pollInterval);
          setRecipeModal(prev => prev ? { ...prev, step_images: imgs } : prev);
          setGalleryGenerating(prev => { const n = { ...prev }; delete n[recipeUrl]; return n; });
          try {
            const p2 = JSON.parse(localStorage.getItem(POLL_KEY) || '{}');
            delete p2[recipeUrl]; localStorage.setItem(POLL_KEY, JSON.stringify(p2));
          } catch {}
        }
      } catch {}
    }, 15000);
  };

  const refreshRecipes = () => {
    supabase
      .from('recipes')
      .select('id,name,url,uploader,upload_date,ingredients,ingredients_measured,ingredients_sources,ingredient_groups,steps,tips,servings,tags,reviews,source,status,thumbnail_url,language,step_images,slug,created_at')
      .eq('language', 'kr')
      .then(({ data }) => {
        if (data) {
          applyRecipeData(data);
          try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch {}
        }
      })
      .catch(() => {});
  };

  // Supabase에서 레시피 로드 (캐시 → 즉시 표시 후 백그라운드 갱신)
  useEffect(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) applyRecipeData(JSON.parse(cached));
    } catch {}

    const loadingTimeout = setTimeout(() => setRecipeLoading(false), 10000);

    supabase
      .from('recipes')
      .select('id,name,url,uploader,upload_date,ingredients,ingredients_measured,ingredients_sources,ingredient_groups,steps,tips,servings,tags,reviews,source,status,thumbnail_url,language,step_images,slug,created_at')
      .eq('language', 'kr')
      .then(({ data }) => {
        clearTimeout(loadingTimeout);
        if (data) {
          applyRecipeData(data);
          try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch {}
        } else {
          setRecipeLoading(false);
        }
      })
      .catch(() => {
        clearTimeout(loadingTimeout);
        setRecipeLoading(false);
      });

    return () => clearTimeout(loadingTimeout);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const showToast = (message) => {
    const key = Date.now();
    setToast({ message, key });
    setTimeout(() => setToast(t => t?.key === key ? null : t), 2500);
  };

  const currentUid = socialUser?.uid || creatorUser?.uid || null;
  const bookmarkUid = socialUser?.uid || creatorUser?.supabaseId || null;
  const [bookmarkNotes, setBookmarkNotes] = useState({});

  const fetchSubscription = async (uid) => {
    if (!uid) { setSubscriptionStatus(null); return; }
    try {
      const res = await fetch(`${API_BASE}/billing/status?userId=${uid}`);
      const data = await res.json();
      setSubscriptionStatus(data.subscription || null);
    } catch { setSubscriptionStatus(null); }
  };

  useEffect(() => { fetchSubscription(bookmarkUid); }, [bookmarkUid]); // eslint-disable-line react-hooks/exhaustive-deps

  const isPremium = (() => {
    if (!subscriptionStatus) return false;
    const { status, trial_ends_at } = subscriptionStatus;
    if (status === 'active') return true;
    if (status === 'trialing' && trial_ends_at) return new Date() < new Date(trial_ends_at);
    return false;
  })();

  // Supabase Auth 세션 감지 (INITIAL_SESSION으로 자동로그인 + 토스트 처리)
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'INITIAL_SESSION') {
        if (session) {
          _applySession(session);
          const name = session.user?.user_metadata?.full_name || session.user?.user_metadata?.name || '';
          const key = Date.now();
          setToast({ message: name ? `다시 오셨군요, ${name}님!` : '다시 오셨군요!', key });
          setTimeout(() => setToast(t => t?.key === key ? null : t), 2500);
        }
        setSocialLoading(false);
      } else if (event === 'SIGNED_IN' && session) {
        _applySession(session);
        setSocialLoading(false);
        // OAuth 콜백 토큰이 URL 해시에 남지 않도록 즉시 제거
        if (window.location.hash.includes('access_token')) {
          window.history.replaceState({}, '', window.location.pathname + window.location.search);
        }
        if (!session.user?.email?.endsWith('@findish.internal')) {
          const name = session.user?.user_metadata?.full_name || session.user?.user_metadata?.name || '';
          const key = Date.now();
          setToast({ message: name ? `로그인됐어요, ${name}님!` : '로그인됐어요!', key });
          setTimeout(() => setToast(t => t?.key === key ? null : t), 2500);
        }
      } else if (event === 'SIGNED_OUT') {
        _clearSession();
      }
    });
    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function _applySession(session) {
    const user = session.user;
    if (user.email?.endsWith('@findish.internal')) {
      const alias = user.email.split('@')[0];
      const userData = {
        uid: `creator:${alias}`,
        supabaseId: user.id,
        username: alias,
        uploaderName: user.user_metadata?.name || alias,
        provider: 'creator',
      };
      setCreatorUser(userData);
      localStorage.setItem('findish_creator', JSON.stringify(userData));
    } else {
      const userData = {
        uid: user.id,
        name: user.user_metadata?.full_name || user.user_metadata?.name || '',
        photoURL: user.user_metadata?.avatar_url || user.user_metadata?.picture || '',
        email: user.email || '',
        provider: user.app_metadata?.provider || 'kakao',
      };
      setSocialUser(userData);
      localStorage.setItem('findish_social', JSON.stringify(userData));
      identifyUser(userData);
      track("signup_completed", { provider: userData.provider });

      // Attribution 저장: localStorage의 ref → DB user_creators (first-touch)
      try {
        const stored = JSON.parse(localStorage.getItem('findish_referral') || 'null');
        if (stored?.creator && Date.now() < stored.expires) {
          fetch(`${API_BASE}/attribution/link`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: userData.uid,
              creator_slug: stored.creator,
              source: 'ref_param',
            }),
          }).then(r => r.json()).then(data => {
            if (data.ok) localStorage.removeItem('findish_referral');
          }).catch(() => {});
        }
      } catch {}
    }
  }

  // 북마크 Supabase 동기화
  useEffect(() => {
    if (!bookmarkUid) { setSavedRecipes([]); setBookmarkNotes({}); notifyBookmarksChanged(0); return; }
    supabase.from('bookmarks').select('recipe_id,recipe_url,note').eq('user_id', bookmarkUid).eq('bookmarked', true)
      .then(({ data }) => {
        if (data) {
          const ids = data.map(r => r.recipe_id || r.recipe_url);
          setSavedRecipes(ids);
          const notes = {};
          data.forEach(r => { if (r.note) notes[r.recipe_id || r.recipe_url] = r.note; });
          setBookmarkNotes(notes);
          notifyBookmarksChanged(ids.length);
        }
      });
  }, [bookmarkUid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Attribution: 비회원일 때 ?ref=X 방문마다 localStorage 덮어쓰기 (last-touch, 30일 TTL).
  // 회원가입 시점에 /attribution/link로 넘겨서 user_creators에 lock. DB UNIQUE(user_id)로 lock 후엔 안 바뀜.
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const ref = params.get('ref');
      if (!ref) return;

      const chefKey = slugToKey[ref];
      if (!chefKey) return;

      if (creatorUser?.alias === ref) return;

      localStorage.setItem('findish_referral', JSON.stringify({
        creator: ref,
        expires: Date.now() + 30 * 24 * 60 * 60 * 1000,
      }));
      track("referral_link_visit", { creator: ref });
    } catch {}
  }, [location.search, creatorUser?.alias]); // eslint-disable-line react-hooks/exhaustive-deps

  // 크리에이터 페이지 진입 시 스크롤을 맨 위로
  useEffect(() => {
    if (slug) window.scrollTo(0, 0);
  }, [slug]);

  // 레거시 앵커 링크 (#about) 리다이렉트
  useEffect(() => {
    if (!slug && location.hash === '#about') navigate('/about', { replace: true });
  }, [slug, location.hash, navigate]);

  // 다른 페이지(MyPage/AdminSettlements)에서 Header 클릭 시 넘어오는 URL 파라미터 처리
  useEffect(() => {
    if (slug) return;
    const params = new URLSearchParams(location.search);
    const hasHandledParam = ['tab', 'analyze', 'login'].some(k => params.has(k));
    if (!hasHandledParam) return;

    if (params.get('tab') === 'saved') setActiveTab('saved');
    if (params.get('analyze') === '1') setAnalyzeOpen(true);
    if (params.get('login') === '1') setLoginModalOpen(true);

    ['tab', 'analyze', 'login'].forEach(k => params.delete(k));
    const q = params.toString();
    navigate({ pathname: '/', search: q ? `?${q}` : '' }, { replace: true });
  }, [location.search, slug, navigate]);

  const [modalEditMode, setModalEditMode] = useState(false);
  const [noteEditingUrl, setNoteEditingUrl] = useState(null);
  const noteEditingRef = useRef(false);
  const [editDraftInit, setEditDraftInit] = useState({ mainIngredients: '', seasonings: '', steps: '', tip: '', servings: '' });
  const [deletedKeys, setDeletedKeys] = useState(() => {
    try {
      const stored = sessionStorage.getItem('findish_deleted_urls');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch { return new Set(); }
  });
  const [confirmDialog, setConfirmDialog] = useState(null); // { message, onConfirm }

  const currentRawData = useMemo(() =>
    (language === "en" ? menuData_en : recipeData)
      .filter(item => !CHEF_FILTER || item.uploader === CHEF_FILTER),
    [language, recipeData, CHEF_FILTER]
  );

  const sortedData = useMemo(() =>
    [...currentRawData]
      .sort((a, b) => new Date(b.upload_date) - new Date(a.upload_date))
      .map(item => {
        const edit = recipeEdits[item.id];
        let ingredients = item.ingredients || [];
        if (edit && (edit.mainIngredients?.length > 0 || edit.seasonings?.length > 0)) {
          ingredients = [...(edit.mainIngredients || []), ...(edit.seasonings || [])];
        }
        const sourcesRaw = item.ingredients_sources || [];
        const ingMeasuredMap = {};
        const ingSourcesMap = {};
        // ingredients_measured는 "재료명 용량" 형식이므로 이름 prefix로 매칭 (인덱스 어긋남 방지)
        (item.ingredients_measured || []).forEach(full => {
          if (!full) return;
          const matched = (item.ingredients || []).find(ing => full.startsWith(ing.trim() + ' '));
          if (matched) ingMeasuredMap[matched.trim()] = full;
        });
        // sources는 prefix 없으므로 원본 ingredients 인덱스 기준
        (item.ingredients || []).forEach((ing, idx) => {
          if (sourcesRaw[idx]) ingSourcesMap[ing.trim()] = sourcesRaw[idx];
        });
        const orderedIngredients = Array.isArray(ingredients) ? [...ingredients] : [];
        return {
          ...item,
          name: edit?.name || item.name,
          hidden: edit?.hidden !== undefined ? edit.hidden : item.status === 'hidden',
          thumbnail: thumbnailOverrides[item.id] || item.thumbnail_url || item.thumbnail || null,
          ingredients: orderedIngredients,
          ingredients_measured: orderedIngredients.map(ing => ingMeasuredMap[ing] || ''),
          ingredients_sources: orderedIngredients.map(ing => ingSourcesMap[ing] || ''),
        };
      }),
    [currentRawData, recipeEdits, thumbnailOverrides]
  );

  const isCreator = !!creatorUser;
  const isAdmin = socialUser?.uid === ADMIN_UID;
  const validRecipes = useMemo(() =>
    sortedData.filter(item =>
      isValidRecipe(item) && (!item.hidden || isCreator || isAdmin)
    ),
    [sortedData, isCreator, isAdmin]
  );

  const POPULAR_TAG_BLOCKLIST = new Set([
    "소금", "후추", "설탕", "물", "식용유", "올리브 오일", "올리브오일", "올리브유",
    "다진 마늘", "마늘", "간장", "참기름", "들기름", "식초", "고추장", "된장", "쌈장",
    "고춧가루", "깨", "참깨", "후춧가루", "흑후추", "백후추",
    "salt", "pepper", "sugar", "water", "olive oil", "garlic", "soy sauce",
    "black pepper", "white pepper", "sesame oil", "vinegar",
    "파슬리", "바질", "로즈마리", "타임", "오레가노",
  ]);

  const popularTags = useMemo(() => {
    if (!CHEF_FILTER) {
      return language === "kr"
        ? ["계란", "삼겹살", "두부", "연어", "파스타", "새우", "소고기", "김치", "감자", "닭가슴살"]
        : ["Egg", "Pork belly", "Tofu", "Salmon", "Pasta", "Shrimp", "Beef", "Kimchi", "Potato", "Chicken breast"];
    }
    const freq = {};
    validRecipes.forEach(recipe => {
      (recipe.ingredients || []).forEach(ing => {
        const key = ing.replace(/\s*\(.*?\)\s*/g, "").trim();
        if (key && !POPULAR_TAG_BLOCKLIST.has(key)) {
          freq[key] = (freq[key] || 0) + 1;
        }
      });
    });
    return Object.entries(freq)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([name]) => name);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, validRecipes]);

  const [searchResults, setSearchResults] = useState(sortedData);
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [searchInputValue, setSearchInputValue] = useState("");

  // 입력 → App 상태 전달은 transition으로 감싸 타이핑 렌더를 블로킹하지 않게 함
  const handleSearchInputChange = React.useCallback((val) => {
    React.startTransition(() => setSearchInputValue(val));
  }, []);

  // 언어 변경 시 검색 결과 및 선택 재료 초기화
  useEffect(() => {
    setSearchResults(sortedData);
    setSelectedIngredients([]);
    setSearchActive(false);
    setSearchInputValue("");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  // Supabase fetch 완료 또는 편집(recipeEdits) 변경 후 searchResults 동기화
  useEffect(() => {
    if (!searchActive && selectedIngredients.length === 0) {
      setSearchResults(sortedData);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipeData, recipeEdits, CHEF_FILTER]);

  const toggleSave = async (recipeId) => {
    if (!bookmarkUid) { setLoginModalOpen(true); return; }
    const isSaved = savedRecipes.includes(recipeId);
    const next = isSaved ? savedRecipes.filter(u => u !== recipeId) : [...savedRecipes, recipeId];
    setSavedRecipes(next);
    notifyBookmarksChanged(next.length);
    try {
      if (isSaved) {
        await supabase.from('bookmarks').update({ bookmarked: false }).eq('user_id', bookmarkUid).eq('recipe_id', recipeId);
      } else {
        await supabase.from('bookmarks')
          .upsert({ user_id: bookmarkUid, recipe_id: recipeId, bookmarked: true }, { onConflict: 'user_id,recipe_id' });
        track("bookmark_added", { recipe_id: recipeId });
      }
      showToast(isSaved ? '저장 해제되었습니다.' : '저장되었습니다.');
    } catch {
      setSavedRecipes(savedRecipes);
      notifyBookmarksChanged(savedRecipes.length);
      showToast('저장에 실패했습니다. 다시 시도해주세요.');
    }
  };

  const saveBookmarkNote = async (recipeId, note) => {
    if (!bookmarkUid) return;
    setBookmarkNotes(prev => ({ ...prev, [recipeId]: note }));
    await supabase.from('bookmarks').update({ note: note || null }).eq('user_id', bookmarkUid).eq('recipe_id', recipeId);
  };

  const handleLoginSuccess = async (session) => {
    await supabase.auth.setSession(session);
    setLoginModalOpen(false);
  };

  const _clearSession = () => {
    setSocialUser(null);
    setCreatorUser(null);
    localStorage.removeItem('findish_social');
    localStorage.removeItem('findish_creator');
    setSocialLoading(false);
    resetUser();
  };

  const saveThumbnailOverride = (recipeId, thumbnailUrl) => {
    const updated = { ...thumbnailOverrides, [recipeId]: thumbnailUrl };
    setThumbnailOverrides(updated);
    localStorage.setItem('findish_thumbnails', JSON.stringify(updated));
    fetch(`${API_BASE}/api/recipes`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-Creator-Uid': creatorUser?.uid || '' },
      body: JSON.stringify({ recipe_id: recipeId, thumbnail_url: thumbnailUrl }),
    }).catch(() => {});
  };

  const clearThumbnailOverride = (recipeId) => {
    const updated = { ...thumbnailOverrides };
    delete updated[recipeId];
    setThumbnailOverrides(updated);
    localStorage.setItem('findish_thumbnails', JSON.stringify(updated));
    fetch(`${API_BASE}/api/recipes`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-Creator-Uid': creatorUser?.uid || '' },
      body: JSON.stringify({ recipe_id: recipeId, thumbnail_url: null }),
    }).catch(() => {});
  };


  const openEditMode = (recipe) => {
    const all = [...new Set(recipe.ingredients || [])];
    const mainList = all.filter(ing => !SEASONINGS.has(normalizeIng(ing)));
    const seasoningList = all.filter(ing => SEASONINGS.has(normalizeIng(ing)));
    const rawSteps = recipe.steps || [];
    const cleanedSteps = rawSteps.map(s =>
      s.replace(/^\d+[.)]\s*/, '').replace(/\n/g, ' ').trim()
    ).filter(Boolean);
    setEditDraftInit({
      name: recipe.name || '',
      mainIngredients: [...mainList, ...seasoningList].join('\n'),
      seasonings: '',
      steps: cleanedSteps.join('\n'),
      tip: (recipe.tips || []).join('\n'),
      servings: recipe.servings || '',
    });
    setModalEditMode(true);
  };

  const saveEditDraft = (recipeId, draft, measuredAmountMap = {}) => {
    const newIngredients = draft.mainIngredients.split('\n').map(s => s.trim()).filter(Boolean);
    const newSteps = draft.steps.split('\n').map(s => s.trim()).filter(Boolean);
    const newTips = draft.tip.trim() ? [draft.tip.trim()] : [];
    const newMeasured = newIngredients.map(ing => {
      const amount = measuredAmountMap[ing];
      return amount ? `${ing} ${amount}` : '';
    });
    const payload = {
      name: draft.name?.trim() || '',
      ingredients: newIngredients,
      ingredients_measured: newMeasured,
      steps: newSteps,
      tips: newTips,
      servings: draft.servings?.trim() || '',
    };
    setModalEditMode(false);
    setRecipeModal(prev => prev ? { ...prev, ...payload } : prev);
    setRecipeData(prev => prev.map(r => r.id === recipeId ? { ...r, ...payload } : r));

    if (isAdmin) {
      fetch(`${API_BASE}/admin-update-recipe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: socialUser.uid, recipe_id: recipeId, payload }),
      }).then(r => r.json()).then(d => {
        if (!d.ok) console.error('어드민 편집 저장 실패:', d.error);
        else showToast('저장됐어요!');
      }).catch(e => console.error('어드민 편집 요청 실패:', e));
    } else {
      supabase.from('recipes').update(payload).eq('id', recipeId).then(({ error }) => {
        if (error) console.error('레시피 편집 저장 실패:', error.message);
        else showToast('저장됐어요!');
      });
    }
  };

  const toggleHidden = (e, recipeId) => {
    e.stopPropagation();
    const existing = recipeEdits[recipeId] || {};
    const newHidden = !existing.hidden;
    const updates = { ...existing, hidden: newHidden };
    const updated = { ...recipeEdits, [recipeId]: updates };
    setRecipeEdits(updated);
    localStorage.setItem('findish_recipe_edits', JSON.stringify(updated));
    supabase.from('recipes').update({
      status: newHidden ? 'hidden' : 'active',
    }).eq('id', recipeId).then(({ error }) => {
      if (error) console.error('공개/비공개 저장 실패:', error.message);
    });
  };

  const adminToggleHidden = (e, recipe) => {
    e.stopPropagation();
    const newStatus = recipe.status === 'hidden' ? 'active' : 'hidden';
    fetch(`${API_BASE}/admin-update-recipe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uid: socialUser.uid, recipe_id: recipe.id, payload: { status: newStatus } }),
    }).then(r => r.json()).then(d => {
      if (!d.ok) { console.error('어드민 공개/비공개 실패:', d.error); return; }
      setRecipeModal(prev => prev ? { ...prev, status: newStatus } : prev);
      setRecipeData(prev => prev.map(r => r.id === recipe.id ? { ...r, status: newStatus } : r));
    }).catch(e => console.error('어드민 공개/비공개 요청 실패:', e));
  };

  // ── 동의어 맵: 같은 재료의 다른 표기 → 검색/표시 통일 ──
  // (cleanup_ingredients.py와 동일한 규칙 유지)
  const synonymMap = {
    // 올리브 오일
    "올리브오일": "올리브 오일", "올리브유": "올리브 오일",
    "엑스트라 버진 올리브오일": "올리브 오일", "엑스트라버진 올리브오일": "올리브 오일",
    "엑스트라버진 올리브 오일": "올리브 오일", "엑스트라버진 오일": "올리브 오일",
    "엑스트라버진 올리브유": "올리브 오일", "엑스트라 버진오일": "올리브 오일",
    "엑스트라버진오일": "올리브 오일", "엑스트라 버진 올리브 오일": "올리브 오일",
    "퓨어 올리브 오일": "올리브 오일", "퓨어올리브오일": "올리브 오일",
    "퓨어올리브유": "올리브 오일", "퓨어 올리브유": "올리브 오일",
    "콩피오일": "올리브 오일", "콩피 오일": "올리브 오일",
    // 올리브 열매
    "그린올리브": "올리브", "그린 올리브": "올리브", "블랙올리브": "올리브",
    "타쟈스카 올리브": "올리브", "패키지 올리브": "올리브",
    // 고춧가루
    "고추가루": "고춧가루", "청양고춧가루": "고춧가루",
    "고운고추가루": "고춧가루", "굵은 고추가루": "고춧가루", "고운고춧가루": "고춧가루",
    // 계란
    "달걀": "계란", "감동란": "계란", "감 동란": "계란",
    "계란 노른자": "계란", "달걀 노른자": "계란", "달걀 (노른자만)": "계란",
    "노른자": "계란", "달걀 흰자": "계란", "반숙란": "계란",
    // 계피
    "계피가루": "계피", "계피스틱": "계피",
    // 김치
    "배추 김치": "김치", "배추김치": "김치", "신김치": "김치",
    // 버터
    "무염 버터": "버터", "무염버터": "버터", "기버터": "버터",
    "가염버터": "버터", "버터(가염)": "버터",
    // 마늘
    "다진 마늘": "마늘", "편마늘": "마늘", "마늘 파우더": "마늘",
    // 대파
    "대파 녹색부분": "대파", "대파 흰부분": "대파", "파": "대파",
    // 양파
    "양파분말": "양파", "적양파": "양파", "보라색 양파": "양파",
    // 파마산 치즈
    "파마산 치즈 가루": "파마산 치즈", "파마산치즈": "파마산 치즈",
    "파마산": "파마산 치즈", "파르메산 치즈": "파마산 치즈", "레지아노 치즈": "파마산 치즈",
    // 파프리카
    "빨간 파프리카": "파프리카", "노란 파프리카": "파프리카",
    "미니 파프리카": "파프리카", "초록 파프리카": "파프리카", "레드파프리카": "파프리카",
    // 방울토마토
    "방울 토마토": "방울토마토", "컬러방울토마토": "방울토마토",
    "달짝이 토마토": "방울토마토", "달짝이토마토": "방울토마토", "무지개 방울토마토": "방울토마토",
    // 선드라이토마토
    "선드라이 토마토": "선드라이토마토", "썬드라이 토마토": "선드라이토마토", "썬드라이토마토": "선드라이토마토",
    // 토마토홀
    "캔 토마토": "토마토홀", "토마토캔": "토마토홀", "홀 토마토 캔": "토마토홀", "무띠 토마토홀": "토마토홀",
    // 토마토퓨레
    "토마토 퓨레": "토마토퓨레", "무띠 토마토퓨레": "토마토퓨레",
    "토마토 페이스트": "토마토퓨레", "토마토페이스트": "토마토퓨레",
    // 토마토소스
    "토마토 소스": "토마토소스", "로제 토마토 소스": "토마토소스",
    // 파슬리
    "이탈리안 파슬리": "파슬리", "다진 파슬리": "파슬리", "파슬리잎": "파슬리",
    // 식초
    "사과식초": "사과 식초", "두배 사과식초": "사과 식초", "쉐리식초": "식초",
    "발사믹식초": "발사믹 식초",
    "발사믹 비네거": "발사믹 식초", "발사믹 비니거": "발사믹 식초",
    "레드 와인 비네거": "레드 와인 식초", "레드와인 비네거": "레드와인 식초",
    "와인식초": "와인 식초",
    // 화이트 발사믹
    "화이트 발사믹 식초": "화이트 발사믹", "화이트 발사믹식초": "화이트 발사믹",
    "화이트발사믹식초": "화이트 발사믹", "화이트발사믹": "화이트 발사믹",
    "화이트 발사믹 글레이즈": "화이트 발사믹", "화이트와인 비니거": "화이트 발사믹",
    "화이트 와인 비네거": "화이트 발사믹", "화이트 와인 비니거": "화이트 발사믹",
    "화이트와인 비네거": "화이트 발사믹", "화이트 와인식초": "화이트 발사믹",
    // 버섯류
    "양송이버섯": "양송이 버섯", "양송이": "양송이 버섯",
    "새송이버섯": "새송이 버섯", "느타리버섯": "느타리 버섯",
    "팽이버섯": "팽이 버섯", "포르치니버섯": "포르치니 버섯",
    "표고버섯": "표고 버섯", "잎새버섯": "잎새 버섯",
    // 랍스터
    "랍스터 테일": "랍스터", "랍스타": "랍스터",
    // 채소 오타/표기
    "살롯": "샬롯",
    "래디시": "래디쉬", "레디시": "래디쉬",
    "사프론": "사프란", "샤프론": "사프란",
    "탈리아탈레": "탈리아텔레", "딸리아딸레": "탈리아텔레",
    "만가닥버섯": "만가닥 버섯",
    "옥수수콘": "옥수수 콘",
    "그린빈": "그린 빈",
    "궁채장아찌": "궁채 장아찌",
    // 무
    "무우": "무",
    // 라임
    "라임 주스": "라임", "라임주스": "라임", "라임제스트": "라임",
    "라임 제스트": "라임", "라임즙": "라임", "라임/레몬 제스트": "라임",
    // 레몬
    "레몬즙": "레몬", "레몬주스": "레몬", "레몬제스트": "레몬",
    "레몬 제스트": "레몬", "레몬껍질": "레몬",
    // 마늘
    "마늘분말": "마늘", "흑마늘": "마늘", "다진마늘": "마늘",
    "통마늘": "마늘", "생마늘": "마늘",
    // 연어
    "노르웨이 생연어": "연어", "껍질 있는 연어": "연어", "껍질 없는 연어": "연어",
    "훈제연어": "연어", "연어 스테이크": "연어", "동원 썸씽스페셜 훈제연어": "연어",
    // 바질
    "바질잎": "바질",
    // 소금
    "맛소금": "소금", "구운 소금": "소금", "죽염": "소금", "샐러리 소금": "소금",
    // 간장
    "맛간장": "간장", "양조간장": "간장", "진간장": "간장",
    "국간장": "간장", "백간장": "간장", "어간장": "간장", "청장(맑은 간장)": "간장",
    // 밥
    "백미": "밥", "즉석밥": "밥", "통곡물밥": "밥", "쌀밥": "밥",
    // 쌀
    "생쌀": "쌀", "불린 쌀": "쌀", "이탈리아 쌀": "쌀", "한국 쌀": "쌀",
    // 메밀가루
    "통메밀가루": "메밀가루",
    // 치즈류
    "부라타치즈": "부라타 치즈", "블루치즈": "블루 치즈", "페타치즈": "페타 치즈",
    "에멘탈치즈": "에멘탈 치즈", "그뤼에르치즈": "그뤼에르 치즈",
    "리코타치즈": "리코타 치즈", "마스카포네치즈": "마스카포네 치즈",
    "마카포네 치즈": "마스카포네 치즈", "마스카포네 크림": "마스카포네 치즈",
    "모짜렐라 슈레드치즈": "모짜렐라 치즈", "프레시 모짜렐라 치즈": "모짜렐라 치즈",
    "슈레드 모짜렐라": "모짜렐라 치즈",
    "폰탈치즈": "폰탈 치즈", "벨큐브치즈": "벨큐브 치즈",
    "파다노": "그라나 파다노 치즈", "그라노파다노": "그라나 파다노 치즈",
    // 코인육수
    "꽃게 코인육수": "코인 육수", "디포리 코인육수": "코인 육수",
    "사골 코인육수": "코인 육수", "채소 코인육수": "코인 육수",
    "채소육수코인": "코인 육수", "사골코인육수": "코인 육수",
    "코인육수사골": "코인 육수", "코인육수": "코인 육수",
    // 당근
    "베이비당근": "당근",
    // 생강
    "생강가루": "생강", "다진 생강": "생강",
    // 알룰로스
    "알루로스": "알룰로스",
    // 마요네즈
    "비건 마요네즈": "마요네즈",
    // 치킨스톡
    "액상 치킨스톡": "치킨스톡", "치킨스톡파우더": "치킨스톡",
    "치킨 육수": "치킨스톡", "치킨육수": "치킨스톡",
    "닭 육수": "치킨스톡", "닭육수": "치킨스톡",
    // 깨
    "깨소금": "깨", "볶은깨": "깨", "볶음깨": "깨",
    "검은깨": "깨", "검정깨": "깨", "참깨": "깨",
    // 아보카도
    "아보카도 퓨레": "아보카도", "아보카도퓨레": "아보카도",
    // 빵
    "통식빵": "식빵", "버거번": "빵",
    // 기타
    "청포도": "포도", "민트잎": "민트", "명란젓": "명란", "갈아만든배": "배",
    "와사비잎": "와사비", "와사비플라워": "와사비", "전복내장": "전복",
    "애플망고": "애플 망고", "물만두": "만두", "왕새우 만두": "만두", "냉동만두": "만두",
    // 후추
    "통후추": "후추", "후추 가루": "후추", "후추가루": "후추",
    "후춧가루": "후추", "흰 후추": "후추", "흰후추": "후추",
    // 트러플
    "트러플 스프레이": "트러플", "트러플 페이스트": "트러플",
    "트러플오일": "트러플", "트러플 오일": "트러플", "화이트 트러플 오일": "트러플",
    // 후리카케
    "후리가게": "후리카케", "후리카게": "후리카케", "후리가케": "후리카케",
    // 와인
    "화이트와인": "화이트 와인", "레드와인": "레드 와인",
    // 페페론치노
    "페퍼론치노": "페페론치노", "페퍼로치니": "페페론치노", "페퍼크러쉬": "페페론치노",
    "페페론치니": "페페론치노", "크러쉬페퍼": "페페론치노",
    "크러쉬드 페퍼": "페페론치노", "페퍼로치노": "페페론치노",
    // 파스타 (종류별 → 파스타 통합)
    "스파게티면": "파스타", "건파스타": "파스타", "생면 파스타": "파스타",
    "숏파스타": "파스타", "파스타 면": "파스타", "파스타 반죽": "파스타",
    "스파게티": "파스타", "스파게티니": "파스타", "리가토니": "파스타",
    "링귀니": "파스타", "탈리아텔레": "파스타", "마팔디네": "파스타",
    "오레끼에떼": "파스타", "펜네 파스타": "파스타",
    // 굴소스
    "우스터소스": "굴소스",
    // 식용유
    "포도씨유": "식용유", "아보카도 오일": "식용유", "아보카도오일": "식용유",
    // 바질 페스토
    "바질페스토": "바질 페스토",
    // 삼겹살
    "K-바비큐 삼겹살": "삼겹살", "국내산 삼겹살": "삼겹살", "미국산 삼겹살": "삼겹살",
    "스페인산 (이베리코) 삼겹살": "삼겹살", "캐나다산 삼겹살": "삼겹살",
    "독일 삼겹살": "삼겹살", "칠레 삼겹살": "삼겹살",
    // 목살
    "목살 스테이크": "목살", "3일 돼지 목살": "목살", "돼지목살": "목살", "돼지 목살": "목살",
    // 돼지고기
    "간 돼지고기": "돼지고기", "돼지고기 등심": "돼지 등심",
    // 닭
    "닭 가슴살": "닭가슴살", "껍질 없는 닭 가슴살": "닭가슴살", "생닭": "닭고기",
    // 케첩
    "토마토 케찹": "케첩", "토마토케찹": "케첩", "토마토 케첩": "케첩",
    // 전분
    "전분 가루": "전분", "옥수수 전분 가루": "전분", "옥수수전분": "전분",
    // 돼지 다짐육
    "돼지고기 다짐육": "돼지 다짐육",
    // 소고기
    "호주산 안심": "안심", "안심 스테이크": "안심", "소고기 안심": "안심",
    "채끝 스테이크": "채끝살", "채끝": "채끝살",
    "등심 스테이크": "소고기 등심", "꽃등심": "소고기 등심",
    "간 소고기": "소고기 다짐육", "다진소고기": "소고기 다짐육", "다진 소고기": "소고기 다짐육",
    "다짐육": "소고기 다짐육", "얇은 소고기": "소고기",
    "한우 우둔": "우둔살",
    // 감자
    "삶은 감자": "감자", "두백감자": "감자", "마리스 파이퍼 감자": "감자",
    // 새우
    "생새우": "새우", "냉동 새우 (적새우살)": "새우", "냉동 새우": "새우",
    // 밀가루
    "일반 밀가루": "밀가루", "프랑스 밀가루 T45": "밀가루",
    // 생크림
    "서울우유 생크림": "생크림", "매일우유 휘핑크림": "생크림",
    // 설탕
    "백설탕": "설탕", "고운 설탕": "설탕", "브라운 설탕": "설탕",
    // 고추
    "청고추": "고추", "홍고추": "고추", "아삭이고추": "고추",
    // 토마토주스
    "토마토 주스": "토마토주스",
    // 셀러리 표기 통일
    "셀러리": "샐러리",
    // 앤초비 표기 통일
    "앤초비": "엔초비",
    // 사프란 표기 통일
    "샤프란": "사프란",
    // 치즈 표기 통일
    "모짜렐라": "모짜렐라 치즈",
    "마스카포네": "마스카포네 치즈", "마스카르포네 치즈": "마스카포네 치즈",
    "리코타": "리코타 치즈",
    // 토마토홀 통일
    "홀 토마토": "토마토홀",
    // 발사믹 통일
    "발사믹": "발사믹 식초",
    // 월계수잎 통일
    "월계수 잎": "월계수잎",
    // 화이트 와인 식초 통일
    "화이트 와인 식초": "화이트 발사믹",
    // 치킨스톡 통일
    "치킨 스톡": "치킨스톡",
    // 올리브유 표기 누락 보완
    "엑스트라 버진 올리브유": "올리브 오일",
    // 돼지 다짐육
    "다진 돼지고기": "돼지 다짐육",
    // 시나몬 → 계피
    "시나몬": "계피",
  };

  // ── 상위어 맵: "돼지고기" 검색 시 삼겹살/목살 등 포함 레시피도 매칭 ──
  // 하지만 삼겹살/목살은 여전히 개별 검색 가능
  // ── 패턴 기반 자동 정규화: 산지 접두사 / 용량 제거 ──
  const autoNormalize = (ing) => {
    let s = ing.trim();
    // 산지 접두사 제거: "국내산 삼겹살" → "삼겹살"
    s = s.replace(/^(국내산|미국산|호주산|캐나다산|스페인산|독일산|칠레산|노르웨이산|프랑스산|이탈리아산|뉴질랜드산|중국산)\s+/, "");
    // 끝부분 용량/수량 제거: "돼지 앞다리살 500g" → "돼지 앞다리살"
    s = s.replace(/\s*\d+([./]\d+)?\s*(g|ml|kg|L|l|개|장|큰술|작은술|컵|스푼|tsp|tbsp|T)\s*$/, "");
    return s.trim();
  };

  // autoNormalize → synonymMap 순서로 정규화 (EN 모드는 소문자 + 복수형 통일)
  // useCallback으로 안정화 — language 변경 시에만 재생성
  const normalizeIng = React.useCallback((ing) => {
    if (language === "en") {
      const lower = ing.trim().toLowerCase();
      const depluraled = EN_PLURAL_MAP[lower] || lower;
      return EN_SYNONYM_MAP[depluraled] || EN_SYNONYM_MAP[lower] || depluraled;
    }
    const auto = autoNormalize(ing);
    return synonymMap[auto] || synonymMap[ing] || auto;
  }, [language]); // eslint-disable-line react-hooks/exhaustive-deps

  // 드롭다운에서 제외할 기본 재료 (너무 흔해서 검색 의미 없음)
  const EXCLUDED_INGREDIENTS = new Set([
    "소금", "후추", "물", "설탕", "밀가루", "기름", "면수",
    "육수", "야채 육수", "채소 육수", "조개 육수", "해물 육수",
    "salt", "pepper", "water", "sugar", "flour", "oil", "pasta water",
    "cooking oil", "kitchen twine",
  ]);


  // 노이즈 재료 필터 (조리 설명 문장 등)
  const isNoisyIngredient = (ing) => {
    if (!ing || ing.length > 25) return true;
    if (/[.。]/.test(ing)) return true;
    if (/준비한다|구워준다|넣는다|볶는다|끓인다|만든다|섞는다|썬다|담는다|자른다|버린다|걸러서|우린다|벗겨|제거한다/.test(ing)) return true;
    const key = language === "en" ? ing.trim().toLowerCase() : ing.trim();
    if (EXCLUDED_INGREDIENTS.has(key)) return true;
    return false;
  };

  // 검색 필터: 상위어 선택 시 하위 재료도 매칭, 개별 재료는 정확 매칭
  const filterMenusByIngredients = (selectedOptions) => {
    return sortedData.filter(menu => {
      const ingredients = menu.ingredients || [];
      const normalizedMenuIngredients = ingredients.map(ing => normalizeIng(ing));

      return selectedOptions.every(opt => {
        const val = typeof opt === "string" ? opt : opt.value;
        const normalizedVal = normalizeIng(val);

        if (parentMap[normalizedVal]) {
          // 상위어: 카테고리 자체 OR 하위 재료 중 하나라도 있으면 매칭
          const allVariants = [normalizedVal, ...parentMap[normalizedVal].map(c => normalizeIng(c))];
          return normalizedMenuIngredients.some(mi => allVariants.includes(mi));
        }

        // 일반 재료: 정규화 후 정확 매칭
        return normalizedMenuIngredients.includes(normalizedVal);
      });
    });
  };

  const handleSearch = (selected) => {
    setSelectedIngredients(selected);
    setSearchActive(selected.length > 0);
    if (selected.length > 0) {
      track("search_ingredients", {
        terms: selected.map(s => s.value),
        count: selected.length,
        groups: [...new Set(selected.map(s => s.group))],
      });
    }
    if (selected.length === 0) {
      setSearchResults(sortedData);
      return;
    }
    const menuNameSelections = selected.filter(s => s.group === "menu");
    const ingredientSelections = selected.filter(s => s.group === "ingredient");
    const textSelections = selected.filter(s => s.group === "text");

    let filtered = sortedData;
    if (menuNameSelections.length > 0) {
      filtered = filtered.filter(item =>
        menuNameSelections.every(s =>
          (item.name || "").toLowerCase().includes(s.value.toLowerCase())
        )
      );
    }
    if (ingredientSelections.length > 0) {
      filtered = filterMenusByIngredients(ingredientSelections).filter(item =>
        filtered.includes(item)
      );
    }
    if (textSelections.length > 0) {
      filtered = filtered.filter(item =>
        textSelections.every(s => {
          const q = s.value.toLowerCase();
          return (
            (item.name || "").toLowerCase().includes(q) ||
            (item.ingredients || []).some(ing => normalizeIng(ing).toLowerCase().includes(q))
          );
        })
      );
    }
    setSearchResults(filtered);
    if (filtered.length === 0) {
      track("search_no_result", {
        terms: selected.map(s => s.value),
        count: selected.length,
      });
    }
  };

  const ingredientOptions = useMemo(() => {
    const allIngredientsRaw = sortedData
      .flatMap((item) => item.ingredients || [])
      .filter((ing) => typeof ing === "string" && ing !== "Only 제품 설명 OR 홍보");
    const ingredientFreq = {};
    for (const ing of allIngredientsRaw) {
      if (isNoisyIngredient(ing)) continue;
      const normalized = normalizeIng(ing);
      if (isNoisyIngredient(normalized)) continue;
      ingredientFreq[normalized] = (ingredientFreq[normalized] || 0) + 1;
    }
    const normalizedIngredientsSet = new Set();
    const options = [];
    const editedIngredientSet = new Set();
    Object.values(recipeEdits).forEach(edit => {
      [...(edit.mainIngredients || []), ...(edit.seasonings || [])].forEach(ing => {
        if (typeof ing === 'string' && ing.trim()) editedIngredientSet.add(normalizeIng(ing.trim()));
      });
    });
    for (const [normalized, count] of Object.entries(ingredientFreq)) {
      if (count <= 2) continue;
      if (!normalizedIngredientsSet.has(normalized)) {
        normalizedIngredientsSet.add(normalized);
        options.push({ value: normalized, label: normalized });
      }
    }
    editedIngredientSet.forEach(normalized => {
      if (normalized && !normalizedIngredientsSet.has(normalized)) {
        normalizedIngredientsSet.add(normalized);
        options.push({ value: normalized, label: normalized });
      }
    });
    if (language === "kr") {
      for (const parent of Object.keys(parentMap)) {
        if (!normalizedIngredientsSet.has(parent)) {
          normalizedIngredientsSet.add(parent);
          options.push({ value: parent, label: parent });
        }
      }
    }
    options.sort((a, b) => a.label.localeCompare(b.label, "ko"));
    return options;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedData, recipeEdits, language]);

  // 메뉴명 옵션 (그룹 분리)
  const menuNameOptions = useMemo(() =>
    validRecipes
      .filter(r => r.name && r.name.trim())
      .map(r => ({ value: r.name, label: r.name, group: "menu" }))
      .filter((opt, idx, arr) => arr.findIndex(o => o.value === opt.value) === idx)
      .sort((a, b) => a.label.localeCompare(b.label, "ko")),
    [validRecipes]
  );

  const groupedSearchOptions = useMemo(() => [
    {
      label: language === "kr" ? "재료" : "Ingredients",
      options: ingredientOptions.map(o => ({ ...o, group: "ingredient" })),
    },
    {
      label: language === "kr" ? "메뉴" : "Dishes",
      options: menuNameOptions,
    },
  ], [language, ingredientOptions, menuNameOptions]);

  // 선택된 태그(재료/텍스트)가 있으면, 해당 레시피에서만 드롭다운 옵션 추출
  const availableGroupedOptions = useMemo(() => {
    if (selectedIngredients.length === 0) return groupedSearchOptions;
    const ingSelections = selectedIngredients.filter(s => s.group === "ingredient");
    const textSelections = selectedIngredients.filter(s => s.group === "text");
    if (ingSelections.length === 0 && textSelections.length === 0) return groupedSearchOptions;

    // 재료 태그로 먼저 필터
    let matchingRecipes = ingSelections.length > 0
      ? filterMenusByIngredients(ingSelections)
      : sortedData.filter(isValidRecipe);

    // 텍스트 태그로 추가 필터
    if (textSelections.length > 0) {
      matchingRecipes = matchingRecipes.filter(item =>
        textSelections.every(s => {
          const q = s.value.toLowerCase();
          return (
            (item.name || "").toLowerCase().includes(q) ||
            (item.ingredients || []).some(ing => normalizeIng(ing).toLowerCase().includes(q))
          );
        })
      );
    }

    const available = new Set();
    for (const recipe of matchingRecipes) {
      for (const ing of recipe.ingredients || []) {
        if (isNoisyIngredient(ing)) continue;
        const normalized = normalizeIng(ing);
        if (!isNoisyIngredient(normalized)) available.add(normalized);
      }
    }
    for (const parent of Object.keys(parentMap)) {
      if (parentMap[parent].some(child => available.has(normalizeIng(child)))) {
        available.add(parent);
      }
    }
    const matchingMenuNames = new Set(matchingRecipes.map(r => r.name));
    return [
      {
        label: groupedSearchOptions[0].label,
        options: groupedSearchOptions[0].options.filter(opt => available.has(opt.value)),
      },
      {
        label: groupedSearchOptions[1].label,
        options: groupedSearchOptions[1].options.filter(opt => matchingMenuNames.has(opt.value)),
      },
    ];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIngredients, recipeEdits]);

const [allMenuSort, setAllMenuSort] = useState("date"); // "name" | "date"
  const [selectedChefs, setSelectedChefs] = useState([]);
  const [selectedServings, setSelectedServings] = useState([]);
  const [selectedFoodType, setSelectedFoodType] = useState([]);
  const [selectedCookStyle, setSelectedCookStyle] = useState([]);
  const [selectedSituation, setSelectedSituation] = useState([]);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const toggleInArray = (setter) => (value) => {
    setter(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
  };

  const TAG_GROUPS = {
    servings:  { label: '분량',     tags: ['1인분','2인분','3~4인분','파티용'] },
    foodType:  { label: '음식 종류', tags: ['파스타','면류','밥','고기','해산물','샐러드','국물요리'] },
    cookStyle: { label: '조리 특성', tags: ['30분 이내','초보 가능','재료해치우기','밀프랩/도시락','에어프라이어','오븐'] },
    situation: { label: '상황',     tags: ['다이어트','술안주','단백질','브런치','야식','아이반찬','손님상'] },
  };

  const chefOptions = useMemo(() =>
    Array.from(
      new Set(validRecipes.map((r) => r.uploader).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b, "ko")),
    [validRecipes]
  );

  // 검색 입력 디바운스 (150ms) — 모바일 성능 개선
  const [debouncedInput, setDebouncedInput] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedInput(searchInputValue), 250);
    return () => clearTimeout(t);
  }, [searchInputValue]);


  // 태그 미선택 + 타이핑 중일 때 라이브 필터링
  const liveFilteredData = useMemo(() => {
    if (searchActive || !debouncedInput.trim()) return null;
    const q = debouncedInput.trim().toLowerCase();
    return sortedData.filter(item =>
      isValidRecipe(item) && (
        (item.name || "").toLowerCase().includes(q) ||
        (item.ingredients || []).some(ing => normalizeIng(ing).toLowerCase().includes(q))
      )
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedInput, searchActive, sortedData]);


  const filteredResults = useMemo(() =>
    (liveFilteredData || searchResults.filter(isValidRecipe))
      .filter(r => !r.hidden || (isCreator && r.uploader === creatorUser?.uploaderName) || isAdmin)
      .filter(r => selectedChefs.length === 0 || selectedChefs.includes(r.uploader))
      .filter(r => selectedServings.length === 0 || (r.tags || []).some(t => selectedServings.includes(t)))
      .filter(r => selectedFoodType.length === 0 || (r.tags || []).some(t => selectedFoodType.includes(t)))
      .filter(r => selectedCookStyle.length === 0 || (r.tags || []).some(t => selectedCookStyle.includes(t)))
      .filter(r => selectedSituation.length === 0 || (r.tags || []).some(t => selectedSituation.includes(t)))
      .filter(r => !deletedKeys.has(r.id)),
    [liveFilteredData, searchResults, isCreator, isAdmin, creatorUser?.uploaderName, selectedChefs, selectedServings, selectedFoodType, selectedCookStyle, selectedSituation, deletedKeys]
  );

  const sortedResults = useMemo(() =>
    [...filteredResults].sort((a, b) =>
      allMenuSort === "name"
        ? (a.name || "").localeCompare(b.name || "", "ko")
        : new Date(b.created_at || b.upload_date) - new Date(a.created_at || a.upload_date)
    ),
    [filteredResults, allMenuSort]
  );

  // 크리에이터 페이지 탭별 레시피
  const creatorTabData = useMemo(() => {
    if (!chefProfile) return null;
    const all = sortedData.filter(r => !deletedKeys.has(r.id) && (!r.hidden || (isCreator && r.uploader === creatorUser?.uploaderName) || isAdmin));
    switch (creatorTab) {
      case 'featured': {
        const ids = chefProfile.featuredIds || [];
        return ids.map(id => all.find(r => r.id === id)).filter(Boolean);
      }
      case 'recent':
        return [...all].sort((a, b) => new Date(b.created_at || b.upload_date) - new Date(a.created_at || a.upload_date));
      case 'popular':
        return [...all].sort((a, b) => new Date(a.created_at || a.upload_date) - new Date(b.created_at || b.upload_date));
      case 'triedit': {
        const ids = chefProfile.triedItIds || [];
        return ids.map(id => all.find(r => r.id === id)).filter(Boolean);
      }
      default:
        return all;
    }
  }, [chefProfile, creatorTab, sortedData, deletedKeys, isCreator, isAdmin, creatorUser?.uploaderName]);

  const displayRecipes = (chefProfile && !searchActive && selectedIngredients.length === 0)
    ? (creatorTabData || [])
    : sortedResults;

  // 레시피 모달 키보드 ← → 내비게이션 (gallery lightbox 열려있을 땐 비활성)
  // /:slug/:recipeSlug 직접 접근 시 해당 레시피 모달 자동 오픈
  useEffect(() => {
    if (!recipeSlug || recipeLoading || !sortedData.length) return;
    const recipe = sortedData.find(r => r.slug === recipeSlug);
    if (recipe && !recipeModal) setRecipeModal(recipe);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipeSlug, recipeLoading, sortedData.length]);

  useEffect(() => {
    if (!recipeModal || galleryLightbox) return;
    const navList = activeTab === 'saved'
      ? validRecipes.filter(item => savedRecipes.includes(item.id) && !deletedKeys.has(item.id))
      : sortedResults;
    const handler = (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (document.activeElement?.tagName === 'TEXTAREA' || document.activeElement?.tagName === 'INPUT') return;
      const idx = navList.findIndex(r => r.id === recipeModal.id);
      if (idx === -1) return;
      const next = e.key === 'ArrowLeft' ? navList[idx - 1] : navList[idx + 1];
      if (next) { setRecipeModal(next); setModalVideoPlaying(false); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [recipeModal, galleryLightbox, sortedResults, activeTab, validRecipes, savedRecipes, deletedKeys]);

  const selectedIngredientValues = useMemo(() =>
    new Set(selectedIngredients.map(s => normalizeIng(s.value).toLowerCase())),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedIngredients]
  );

  const savedSet = useMemo(() => new Set(savedRecipes), [savedRecipes]);

  const duplicateUrls = useMemo(() => {
    const counts = {};
    validRecipes.forEach(item => {
      const url = (item.url || "").trim();
      if (url) counts[url] = (counts[url] || 0) + 1;
    });
    return new Set(Object.keys(counts).filter(u => counts[u] > 1));
  }, [validRecipes]);

  const handleDeleteRecipe = (e, recipeId, url, name) => {
    e.stopPropagation();
    setConfirmDialog({
      message: `'${name}' 레시피를 삭제하시겠습니까?`,
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          const res = await fetch(`${API_BASE}/delete-recipe`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ recipe_id: recipeId, video_url: url, name }),
          });
          const data = await res.json();
          if (data.ok && data.deleted) {
            setDeletedKeys(prev => {
              const next = new Set([...prev, recipeId]);
              try { sessionStorage.setItem('findish_deleted_urls', JSON.stringify([...next])); } catch {}
              return next;
            });
            setSearchResults(prev => prev.filter(r => r.id !== recipeId));
            if (recipeModal?.id === recipeId) setRecipeModal(null);
          }
        } catch {}
      },
    });
  };

  const copyPromoLink = async (item) => {
    const chefSlug = chefConfig[item.uploader]?.slug;
    if (!chefSlug) { showToast('크리에이터 슬러그 없음'); return; }
    const path = item.slug ? `/${chefSlug}/${item.slug}` : `/${chefSlug}`;
    const origin = process.env.REACT_APP_PUBLIC_ORIGIN || 'https://menu-search.vercel.app';
    const url = `${origin}${path}?ref=${chefSlug}`;
    try {
      await navigator.clipboard.writeText(url);
      showToast('홍보 링크 복사됨');
      track('promo_link_copied', { creator: chefSlug, recipe_id: item.id });
    } catch {
      window.prompt('복사 실패 — 직접 복사하세요:', url);
    }
  };

  const makeCardProps = (item) => {
    const ytId = extractYouTubeId(item.url);
    const isMyRecipe = !!(creatorUser && creatorUser.uploaderName === item.uploader);
    const chefSlug = (isMyRecipe || isAdmin) ? chefConfig[item.uploader]?.slug : null;
    return {
      item,
      thumbnailSrc: item.thumbnail || (ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : null),
      isHidden: !!item.hidden,
      isSaved: savedSet.has(item.id),
      isMyRecipe,
      isCreator,
      isAdmin,
      selectedIngredientValues,
      selectedIngredients,
      language,
      normalizeIng,
      onOpen: () => {
        setRecipeModal(item); setModalVideoPlaying(false); setModalEditMode(false);
        track("recipe_opened", {
          recipe_id: item.id,
          recipe_name: item.name,
          uploader: item.uploader,
        });
        if (item.slug && slug) navigate(`/${slug}/${item.slug}`);
        // 모달 열 때 localStorage pending 갤러리 확인 → 폴링 재개
        const POLL_KEY = 'findish_gallery_pending';
        try {
          const pending = JSON.parse(localStorage.getItem(POLL_KEY) || '{}');
          if (pending[item.id]) {
            setGalleryGenerating(prev => ({ ...prev, [item.id]: true }));
            const pollId = item.id;
            const pollInterval = setInterval(async () => {
              try {
                const { data } = await supabase.from('recipes').select('step_images').eq('id', pollId).single();
                const imgs = data?.step_images || {};
                if (Object.keys(imgs).filter(k => k !== 'dish').length > 0) {
                  clearInterval(pollInterval);
                  setRecipeModal(prev => prev ? { ...prev, step_images: imgs } : prev);
                  setGalleryGenerating(prev => { const n = { ...prev }; delete n[pollId]; return n; });
                  const p2 = JSON.parse(localStorage.getItem(POLL_KEY) || '{}');
                  delete p2[pollId]; localStorage.setItem(POLL_KEY, JSON.stringify(p2));
                }
              } catch {}
            }, 15000);
          }
        } catch {}
      },
      onToggleSave: (e) => { e.stopPropagation(); toggleSave(item.id); },
      isDuplicate: duplicateUrls.has((item.url || "").trim()),
      onToggleHidden: (e) => toggleHidden(e, item.id),
      onEdit: (e) => { e.stopPropagation(); setRecipeModal(item); setModalVideoPlaying(false); openEditMode(item); },
      onDelete: (e) => handleDeleteRecipe(e, item.id, item.url, item.name),
      onCopyPromoLink: chefSlug ? (e) => { e.stopPropagation(); copyPromoLink(item); } : undefined,
      onTagClick: (tag) => {
        if (TAG_GROUPS.servings.tags.includes(tag)) setSelectedServings(tag);
        else if (TAG_GROUPS.foodType.tags.includes(tag)) setSelectedFoodType(tag);
        else if (TAG_GROUPS.cookStyle.tags.includes(tag)) setSelectedCookStyle(tag);
        else if (TAG_GROUPS.situation.tags.includes(tag)) setSelectedSituation(tag);
      },
      onChefClick: (uploader) => {
        const chefSlug = chefConfig[uploader]?.slug;
        if (chefSlug) navigate(`/${chefSlug}`);
      },
    };
  };

  const recipeCount = validRecipes.length;

  return (
    <div className={darkMode ? "app dark" : "app light"}>
      <Header
        chefProfile={chefProfile}
        isPremium={isPremium}
        socialLoading={socialLoading}
        onLogoClick={() => {
          if (slug) {
            navigate('/');
            setActiveTab('home');
            setSelectedIngredients([]);
            setSearchActive(false);
            setSearchInputValue('');
          } else {
            setActiveTab('home');
            setSelectedIngredients([]);
            setSearchActive(false);
            setSearchInputValue('');
            setSearchResults(sortedData);
          }
        }}
        onAnalyzeClick={() => setAnalyzeOpen(true)}
        onBookmarkClick={() => setActiveTab(t => t === 'saved' ? 'home' : 'saved')}
        onOpenLogin={() => setLoginModalOpen(true)}
      />
      {/* Analyze Modal */}
      <Modal open={analyzeOpen} onClose={() => setAnalyzeOpen(false)} darkMode={darkMode}>
        <AnalyzePanel apiBase={API_BASE} darkMode={darkMode} onSaveSuccess={refreshRecipes} />
      </Modal>

      {/* Login Modal */}
      <LoginModal
        open={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
        onLoginSuccess={handleLoginSuccess}
        darkMode={darkMode}
      />

      {subscriptionModal && bookmarkUid && (
        <SubscriptionModal
          userId={bookmarkUid}
          darkMode={darkMode}
          onClose={() => setSubscriptionModal(false)}
          onSuccess={() => { setSubscriptionModal(false); fetchSubscription(bookmarkUid); }}
        />
      )}

      {/* Toast */}
      {toast && (
        <div key={toast.key} className={`toast-notification${darkMode ? ' dark' : ''}`}>
          {toast.message}
        </div>
      )}

      {/* Recipe Detail Modal */}
      <Modal open={!!recipeModal} onClose={() => {
        if (modalEditMode) {
          if (!window.confirm('편집 중인 내용이 있습니다. 나가시겠습니까?')) return;
          if (recipeModal?.url) localStorage.removeItem(`findish_draft_${recipeModal.url}`);
        }
        setRecipeModal(null); setModalVideoPlaying(false); setModalEditMode(false); setNoteEditingUrl(null);
        if (recipeSlug && slug) navigate(`/${slug}`);
      }} darkMode={darkMode} hideClose preventClose={() => noteEditingRef.current}>
        {recipeModal && (() => {
          const modalNavList = activeTab === 'saved'
            ? validRecipes.filter(item => savedRecipes.includes(item.id) && !deletedKeys.has(item.id))
            : sortedResults;
          const modalIdx = modalNavList.findIndex(r => r.id === recipeModal.id);
          const showPaywall = !isPremium && !isCreator && !isAdmin;
          return (
          <div className="recipe-modal">
            {modalIdx > 0 && (
              <button className="recipe-modal-nav recipe-modal-nav-prev" onClick={() => { setRecipeModal(modalNavList[modalIdx - 1]); setModalVideoPlaying(false); }}>‹</button>
            )}
            {modalIdx < modalNavList.length - 1 && (
              <button className="recipe-modal-nav recipe-modal-nav-next" onClick={() => { setRecipeModal(modalNavList[modalIdx + 1]); setModalVideoPlaying(false); }}>›</button>
            )}
            <div className="recipe-modal-header">
              <h2 className="recipe-modal-title">{recipeModal.name}</h2>
              {!modalEditMode && (isAdmin || (creatorUser && creatorUser.uploaderName === recipeModal.uploader)) && chefConfig[recipeModal.uploader]?.slug && (
                <button
                  className="recipe-edit-toggle-btn"
                  onClick={() => copyPromoLink(recipeModal)}
                  title="홍보 링크 복사 (?ref 자동 삽입)"
                >
                  링크
                </button>
              )}
              {!modalEditMode && isAdmin && (
                <>
                  <button
                    className={`recipe-edit-toggle-btn${recipeModal.status === 'hidden' ? ' btn-hidden-state' : ''}`}
                    onClick={(e) => adminToggleHidden(e, recipeModal)}
                  >
                    {recipeModal.status === 'hidden' ? '비공개' : '공개'}
                  </button>
                  <button className="recipe-edit-toggle-btn" onClick={() => openEditMode(recipeModal)}>
                    편집
                  </button>
                  <button
                    className="recipe-edit-toggle-btn btn-delete-state"
                    onClick={(e) => handleDeleteRecipe(e, recipeModal.id, recipeModal.url, recipeModal.name)}
                  >
                    삭제
                  </button>
                </>
              )}
              {creatorUser && !modalEditMode && (
                <>
                  {creatorUser.uploaderName === recipeModal.uploader && (
                    <>
                      <button
                        className={`recipe-edit-toggle-btn${recipeEdits[recipeModal.id]?.hidden ? ' btn-hidden-state' : ''}`}
                        onClick={(e) => toggleHidden(e, recipeModal.id)}
                      >
                        {recipeEdits[recipeModal.id]?.hidden ? '비공개' : '공개'}
                      </button>
                      <button className="recipe-edit-toggle-btn" onClick={() => openEditMode(recipeModal)}>
                        편집
                      </button>
                    </>
                  )}
                </>
              )}
              {!modalEditMode && bookmarkUid && (
                <button
                  className={`recipe-modal-bookmark-btn${savedSet.has(recipeModal.id) ? ' saved' : ''}`}
                  onClick={() => toggleSave(recipeModal.id)}
                  title={savedSet.has(recipeModal.id) ? '저장 취소' : '저장하기'}
                >
                  {savedSet.has(recipeModal.id) ? <FaBookmark size={14} /> : <FaRegBookmark size={14} />}
                  {savedSet.has(recipeModal.id) ? '저장됨' : '저장하기'}
                </button>
              )}
              <button className="recipe-modal-close-btn" onClick={() => {
                if (modalEditMode) {
                  if (!window.confirm('편집 중인 내용이 있습니다. 나가시겠습니까?')) return;
                  if (recipeModal?.id) localStorage.removeItem(`findish_draft_${recipeModal.id}`);
                }
                setRecipeModal(null); setModalVideoPlaying(false); setModalEditMode(false); setNoteEditingUrl(null);
                if (recipeSlug && slug) navigate(`/${slug}`);
              }}>
                &times;
              </button>
            </div>
            <div className="recipe-modal-meta">
              <img
                src={channelProfiles[recipeModal.uploader] || ""}
                alt={recipeModal.uploader}
                className="recipe-modal-avatar"
                onError={(e) => { e.target.style.display = "none"; }}
                style={{ cursor: "pointer" }}
                onClick={() => {
                  const chefSlug = chefConfig[recipeModal.uploader]?.slug;
                  if (chefSlug) navigate(`/${chefSlug}`);
                }}
              />
              <div className="recipe-modal-meta-text">
                {recipeModal.uploader && (
                  <span
                    className="recipe-modal-uploader"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      const chefSlug = chefConfig[recipeModal.uploader]?.slug;
                      if (chefSlug) navigate(`/${chefSlug}`);
                    }}
                  >{recipeModal.uploader}</span>
                )}
                {recipeModal.upload_date && (
                  <span className="recipe-modal-date">{recipeModal.upload_date}</span>
                )}
              </div>
            </div>
            {/* 태그 — 업로더/날짜 바로 아래 */}
            {(() => {
              const aiServings = recipeModal.servings || '';
              const aiTags = recipeModal.tags || [];
              const displayServings = (recipeEdits[recipeModal.id] || {}).servings || aiServings;
              const extraTags = aiTags.filter(t => t !== aiServings && t !== displayServings);
              if (!displayServings && extraTags.length === 0) return null;
              return (
                <div className="recipe-tags-section" style={{ marginBottom: 12 }}>
                  {displayServings && (
                    <span className="recipe-tag recipe-tag-servings">{displayServings}</span>
                  )}
                  {extraTags.map((tag, i) => (
                    <span key={i} className="recipe-tag">{tag}</span>
                  ))}
                </div>
              );
            })()}
            {extractYouTubeId(recipeModal.url) && (() => {
              const ytId = extractYouTubeId(recipeModal.url);
              return (
                <div style={{ margin: "0 0 16px" }}>
                  {modalVideoPlaying ? (
                    <div className="recipe-modal-thumb-link playing">
                      <iframe
                        className="recipe-modal-iframe"
                        src={`https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0`}
                        allow="autoplay; encrypted-media"
                        allowFullScreen
                        loading="lazy"
                        title={recipeModal.name}
                      />
                    </div>
                  ) : (
                    <div
                      className="recipe-modal-thumb-link recipe-modal-thumb-play"
                      onClick={() => setModalVideoPlaying(true)}
                    >
                      <img
                        src={`https://img.youtube.com/vi/${ytId}/hqdefault.jpg`}
                        alt={recipeModal.name}
                        className="recipe-modal-iframe"
                        style={{ objectFit: "cover" }}
                      />
                      <div className="recipe-modal-play-overlay">▶</div>
                    </div>
                  )}
                </div>
              );
            })()}
            {/* Paywall blur wrapper */}
            <div style={showPaywall ? { filter: 'blur(5px)', pointerEvents: 'none', userSelect: 'none', minHeight: 200 } : {}}>
            {(() => {
              const displaySteps = recipeModal.steps || [];
              const displayTip = '';

              const all = [...new Set(recipeModal.ingredients || [])];
              // 서버 그룹핑(ingredient_groups)이 있으면 그걸로, 없으면 SEASONINGS 폴백
              const serverGroupsArr = recipeModal.ingredient_groups || [];
              const groupByIng = {};
              (recipeModal.ingredients || []).forEach((ing, i) => {
                const g = serverGroupsArr[i];
                if (g) groupByIng[ing.trim()] = g;
              });
              const classifyGroup = (ing) => {
                // 하이브리드: SEASONINGS에 있으면 무조건 양념 (서버 값 override)
                if (SEASONINGS.has(normalizeIng(ing))) return 'seasoning';
                // SEASONINGS에 없으면 서버 값(Gemini) 존중
                const sg = groupByIng[ing.trim()];
                if (sg === '양념·소스') return 'seasoning';
                return 'main';
              };
              const mainList = all.filter(ing => classifyGroup(ing) === 'main');
              const seasoningList = all.filter(ing => classifyGroup(ing) === 'seasoning');
              const sortedMain = [
                ...mainList.filter(ing => selectedIngredientValues.has(ing.toLowerCase())),
                ...mainList.filter(ing => !selectedIngredientValues.has(ing.toLowerCase())),
              ];
              const sortedSeasoning = [
                ...seasoningList.filter(ing => selectedIngredientValues.has(ing.toLowerCase())),
                ...seasoningList.filter(ing => !selectedIngredientValues.has(ing.toLowerCase())),
              ];
              const measuredAmountMap = {};
              const sourcesMap = {};
              const sourcesArr = recipeModal.ingredients_sources || [];
              // ingredients_measured는 "재료명 용량" 형식이므로 이름 prefix로 매칭
              (recipeModal.ingredients_measured || []).forEach(full => {
                if (!full) return;
                const matched = (recipeModal.ingredients || []).find(ing => full.startsWith(ing.trim() + ' '));
                if (matched) measuredAmountMap[matched.trim()] = full.slice(matched.trim().length + 1).trim();
              });
              (recipeModal.ingredients || []).forEach((ing, idx) => {
                const ingName = ing.trim();
                if (sourcesArr[idx]) {
                  sourcesMap[ingName] = sourcesArr[idx];
                }
              });

              return (
                <>
                  {modalEditMode ? (
                    <RecipeEditPanel
                      key={recipeModal.id}
                      initialDraft={editDraftInit}
                      darkMode={darkMode}
                      t={t}
                      thumbnailUrl={thumbnailOverrides[recipeModal.id] || recipeModal.thumbnail_url || ''}
                      uploaderName={recipeModal.uploader}
                      onSave={draft => saveEditDraft(recipeModal.id, draft, measuredAmountMap)}
                      onCancel={() => setModalEditMode(false)}
                      onSaveThumbnail={url => saveThumbnailOverride(recipeModal.id, url)}
                      onClearThumbnail={() => clearThumbnailOverride(recipeModal.id)}
                    />
                  ) : (
                    <>

                      {/* TIP 섹션 */}
                      {(() => {
                        const aiTips = recipeModal.tips || [];
                        if (displayTip) {
                          return (
                            <div className="recipe-tip-section">
                              <div className="recipe-tip-label-wrap">
                                <span className="recipe-tip-label">
                                  {recipeModal.uploader ? `${recipeModal.uploader}'s TIP` : t.tip}
                                </span>
                                {recipeModal.uploader && (
                                  <span
                                    className="recipe-tip-info"
                                    data-tooltip={language === 'kr' ? `${recipeModal.uploader}가 직접 작성한 팁이에요!` : `Written directly by ${recipeModal.uploader}!`}
                                  >?</span>
                                )}
                              </div>
                              <p className="recipe-tip-text">
                                {displayTip.split('\n').map((line, i) => (
                                  <span key={i} className="recipe-tip-line">"{line}"</span>
                                ))}
                              </p>
                            </div>
                          );
                        }
                        if (aiTips.length > 0) {
                          return (
                            <div className="recipe-tip-section">
                              <div className="recipe-tip-label-wrap">
                                <span className="recipe-tip-label">
                                  {recipeModal.uploader ? `${recipeModal.uploader}'s TIP` : t.tip}
                                </span>
                                <span
                                  className="recipe-tip-info"
                                  data-tooltip={language === 'kr' ? 'AI가 영상에서 추출한 크리에이터 팁이에요!' : 'Tips extracted from the video by AI!'}
                                >?</span>
                              </div>
                              <div className="recipe-tip-text">
                                {aiTips.map((tip, i) => (
                                  <span key={i} className="recipe-tip-line">"{tip}"</span>
                                ))}
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div className="recipe-tip-section">
                            <div className="recipe-tip-label-wrap">
                              <span className="recipe-tip-label">
                                {recipeModal.uploader ? `${recipeModal.uploader}'s TIP` : t.tip}
                              </span>
                            </div>
                            <p className="recipe-tip-text" style={{ opacity: 0.4 }}>
                              {language === 'kr' ? '곧 올라올 예정이에요!' : 'Coming soon!'}
                            </p>
                          </div>
                        );
                      })()}

                      {/* RECIPE 섹션 — 주재료/양념·소스 그룹핑 (좌측 액센트 바) */}
                      {(sortedMain.length > 0 || sortedSeasoning.length > 0) && (
                        <div className="recipe-modal-section">
                          <h3 className="recipe-modal-section-title">
                            RECIPE{recipeModal.servings ? ` (${recipeModal.servings})` : ''}
                          </h3>
                          <div className="recipe-ing-groups">
                            {[
                              { key: 'main', label: '주재료', items: sortedMain, isSeasoning: false },
                              { key: 'seasoning', label: '양념·소스', items: sortedSeasoning, isSeasoning: true },
                            ].filter(g => g.items.length > 0).map(g => (
                              <div key={g.key} className={`recipe-ing-group-block ${g.key}`}>
                                <div className="recipe-ing-group-label">{g.label}</div>
                                <div className={`recipe-ing-pills${darkMode ? ' dark' : ''}`}>
                                  {g.items.map((ing, i) => {
                                    const { name, amount: parsedAmount } = parseIngText(ing);
                                    const amount = measuredAmountMap[ing.trim()] || parsedAmount;
                                    const isHighlighted = selectedIngredientValues.has(ing.toLowerCase()) || selectedIngredientValues.has(name.toLowerCase());
                                    const src = sourcesMap[ing.trim()];
                                    const srcLabel = src === '고정댓글' ? '댓글' : src === '더보기란' ? '더보기' : src === '영상 분석' ? '영상' : src ? 'AI' : null;
                                    const srcClass = src === '영상 분석' ? ' video' : (src && src !== '고정댓글' && src !== '더보기란') ? ' ai' : '';
                                    return (
                                      <span key={i} className={`recipe-ing-pill${isHighlighted ? ' highlighted' : ''}${g.isSeasoning ? ' seasoning' : ''}`}>
                                        {name}
                                        {amount && <span className="recipe-ing-pill-amount">{amount}</span>}
                                        {srcLabel && process.env.NODE_ENV === 'development' && <span className={`recipe-ing-pill-src${srcClass}`}>{srcLabel}</span>}
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Instruction 섹션 — 텍스트만 */}
                      <div className="recipe-modal-steps">
                        <h3 className="recipe-modal-section-title">INSTRUCTION</h3>
                        {displaySteps.length > 0 ? (
                          <ol className="recipe-steps-list">
                            {displaySteps.map((step, i) => (
                              <li key={i} className="recipe-step-item">
                                <div className="step-row">
                                  <span className="step-number">{i + 1}</span>
                                  <span className="step-text">{step.replace(/^\d+[.)]\s*/, '')}</span>
                                </div>
                              </li>
                            ))}
                          </ol>
                        ) : (
                          <p className="recipe-modal-no-steps">{t.noSteps}</p>
                        )}
                      </div>

                      {/* Gallery 섹션 — step_images가 있을 때만 표시 */}
                      {(() => {
                        const dishFinalSrc = recipeModal.step_images?.dish_final || null;
                        const stepImgs = Object.entries(recipeModal.step_images || {})
                          .filter(([k]) => k !== 'dish' && k !== 'dish_final')
                          .sort(([a], [b]) => Number(a) - Number(b))
                          .map(([, src], i) => ({ src, index: i }));
                        // dish_final 있으면 마지막에 추가, 없으면 마지막 step을 "완성"으로 표시
                        const allImgs = dishFinalSrc
                          ? [...stepImgs, { src: dishFinalSrc, index: stepImgs.length, isFinal: true }]
                          : stepImgs.map((img, i) => ({ ...img, isFinal: i === stepImgs.length - 1 }));
                        if (stepImgs.length === 0) {
                          const isGenerating = galleryGenerating[recipeModal.id];
                          return (
                            <div className="recipe-modal-section">
                              <h3 className="recipe-modal-section-title">GALLERY</h3>
                              <div style={{ padding: '12px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
                                <button
                                  disabled={isGenerating}
                                  onClick={() => startGalleryGeneration(recipeModal.id, recipeModal.url)}
                                  style={{
                                    padding: '8px 18px', borderRadius: 8, border: '1px solid #ccc',
                                    background: isGenerating ? '#eee' : '#f5f5f5',
                                    cursor: isGenerating ? 'not-allowed' : 'pointer', fontSize: 13,
                                    color: isGenerating ? '#999' : '#333',
                                  }}
                                >
                                  {isGenerating ? '갤러리 생성 중...' : '갤러리 생성'}
                                </button>
                                {isGenerating && (
                                  <span style={{ fontSize: 12, color: '#999' }}>1~2분 소요됩니다. 모달을 닫아도 계속 진행돼요.</span>
                                )}
                              </div>
                            </div>
                          );
                        }
                        const isRegenning = galleryGenerating[recipeModal.id];
                        return (
                          <div className="recipe-modal-section">
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                              <h3 className="recipe-modal-section-title" style={{ margin: 0 }}>GALLERY</h3>
                              {!isRegenning ? (
                                <button
                                  onClick={() => startGalleryGeneration(recipeModal.id, recipeModal.url)}
                                  title="갤러리 재생성"
                                  style={{
                                    padding: '3px 8px', borderRadius: 6, border: '1px solid #ddd',
                                    background: 'transparent', cursor: 'pointer', fontSize: 13,
                                    color: '#aaa', lineHeight: 1,
                                  }}
                                >↺</button>
                              ) : (
                                <span style={{ fontSize: 12, color: '#999' }}>재생성 중... (1~2분)</span>
                              )}
                            </div>
                            <div className="recipe-gallery-grid">
                              {allImgs.map(({ src, index, isFinal }) => (
                                <button
                                  key={index}
                                  className="recipe-gallery-item"
                                  onClick={() => setGalleryLightbox({ items: allImgs, current: index })}
                                >
                                  <img src={src} alt={isFinal ? '완성' : `gallery ${index + 1}`} loading="lazy" />
                                  {isFinal
                                    ? <span className="recipe-gallery-step-num recipe-gallery-final">완성</span>
                                    : <span className="recipe-gallery-step-num">{index + 1}</span>
                                  }
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })()}

                      {/* REVIEWS 섹션 — 유튜브 댓글 후기 (좋아요순, 기본 5개 + 더보기) */}
                      {Array.isArray(recipeModal.reviews) && recipeModal.reviews.length > 0 && (() => {
                        const sorted = [...recipeModal.reviews].sort((a, b) => (b?.likes || 0) - (a?.likes || 0));
                        const DEFAULT_COUNT = 5;
                        const visible = showAllReviews ? sorted : sorted.slice(0, DEFAULT_COUNT);
                        const hiddenCount = sorted.length - visible.length;
                        return (
                          <div className="recipe-modal-section">
                            <h3 className="recipe-modal-section-title">
                              실제 후기 ({sorted.length})
                            </h3>
                            <div className="recipe-reviews-list">
                              {visible.map((r, i) => (
                                <div key={i} className={`recipe-review-item${darkMode ? ' dark' : ''}`}>
                                  <div className="recipe-review-likes">♥ {r?.likes ?? 0}</div>
                                  <div className="recipe-review-text">{r?.text || ''}</div>
                                </div>
                              ))}
                            </div>
                            {sorted.length > DEFAULT_COUNT && (
                              <button
                                type="button"
                                className={`recipe-reviews-toggle${darkMode ? ' dark' : ''}`}
                                onClick={() => setShowAllReviews(v => !v)}
                              >
                                {showAllReviews ? '접기' : `더보기 (+${hiddenCount})`}
                              </button>
                            )}
                          </div>
                        );
                      })()}

                    </>
                  )}

                  {!modalEditMode && bookmarkUid && savedSet.has(recipeModal.id) && (
                    <div className={`bookmark-note-section-wrap${darkMode ? ' dark' : ''}`}>
                      <h3 className="recipe-modal-section-title">MY NOTE</h3>
                      <div
                        className={`bookmark-note-inline${darkMode ? ' dark' : ''}`}
                        onClick={() => setNoteEditingUrl(recipeModal.id)}
                      >
                        {noteEditingUrl === recipeModal.id ? (
                          <textarea
                            className={`bookmark-note-inline-input${darkMode ? ' dark' : ''}`}
                            autoFocus
                            value={bookmarkNotes[recipeModal.id] || ''}
                            maxLength={200}
                            placeholder="메모 추가..."
                            onChange={e => setBookmarkNotes(prev => ({ ...prev, [recipeModal.id]: e.target.value }))}
                            onFocus={() => { noteEditingRef.current = true; }}
                            onKeyDown={e => {
                              if (e.key === 'Escape') {
                                e.preventDefault();
                                saveBookmarkNote(recipeModal.id, e.target.value);
                                setNoteEditingUrl(null);
                                setTimeout(() => { noteEditingRef.current = false; }, 150);
                              }
                            }}
                            onBlur={e => {
                              saveBookmarkNote(recipeModal.id, e.target.value);
                              setNoteEditingUrl(null);
                              setTimeout(() => { noteEditingRef.current = false; }, 150);
                            }}
                          />
                        ) : (
                          <span className={`bookmark-note-inline-text${!bookmarkNotes[recipeModal.id] ? ' empty' : ''}${darkMode ? ' dark' : ''}`}>
                            {bookmarkNotes[recipeModal.id] || '메모 추가...'}
                          </span>
                        )}
                      </div>
                      {noteEditingUrl === recipeModal.id && (
                        <div className="bookmark-note-footer">
                          <span className="bookmark-note-charcount">
                            {(bookmarkNotes[recipeModal.id] || '').length}/200
                          </span>
                          <button
                            className={`bookmark-note-done-btn${darkMode ? ' dark' : ''}`}
                            onMouseDown={e => {
                              e.preventDefault();
                              saveBookmarkNote(recipeModal.id, bookmarkNotes[recipeModal.id] || '');
                              setNoteEditingUrl(null);
                              noteEditingRef.current = false;
                            }}
                          >
                            ✓
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              );
            })()}
            </div>{/* end paywall blur wrapper */}
            {showPaywall && (
              <PaywallModal
                darkMode={darkMode}
                isLoggedIn={!!socialUser}
                channelProfiles={channelProfiles}
                onLogin={() => {
                  setRecipeModal(null);
                  setLoginModalOpen(true);
                }}
                onSubscribe={() => {
                  setSubscriptionModal(true);
                }}
              />
            )}
          </div>
          );
        })()}
      </Modal>

      {/* Gallery Lightbox */}
      {galleryLightbox && (() => {
        const { items, current } = galleryLightbox;
        const item = items[current];
        const goPrev = () => current > 0 && setGalleryLightbox({ items, current: current - 1 });
        const goNext = () => current < items.length - 1 && setGalleryLightbox({ items, current: current + 1 });
        return (
          <div
            className="gallery-lightbox-overlay"
            onClick={() => setGalleryLightbox(null)}
            onKeyDown={e => { if (e.key === 'ArrowLeft') goPrev(); if (e.key === 'ArrowRight') goNext(); if (e.key === 'Escape') setGalleryLightbox(null); }}
            tabIndex={0}
            ref={el => el && el.focus()}
          >
            <div className="gallery-lightbox-box" onClick={e => e.stopPropagation()}>
              <button className="gallery-lightbox-close" onClick={() => setGalleryLightbox(null)}>✕</button>
              {current > 0 && (
                <button className="gallery-lightbox-nav gallery-lightbox-prev" onClick={goPrev}>‹</button>
              )}
              {current < items.length - 1 && (
                <button className="gallery-lightbox-nav gallery-lightbox-next" onClick={goNext}>›</button>
              )}
              <div className="gallery-lightbox-img-wrap">
                <img src={item.src} alt={`gallery ${item.index + 1}`} className="gallery-lightbox-img" />
              </div>
            </div>
          </div>
        );
      })()}

      {activeTab === "chef" ? (
        <ChefAI darkMode={darkMode} />
      ) : activeTab === "saved" ? (
        <div className="saved-tab-container">
          <h2 className="saved-tab-title">
            {language === "kr" ? "저장한 레시피" : "Saved Recipes"}
          </h2>
          {!currentUid ? (
            <div className="saved-tab-empty">
              <FaRegBookmark size={32} style={{ marginBottom: 12, opacity: 0.3 }} />
              <p>{language === "kr" ? "로그인하면 저장한 레시피를 모아볼 수 있어요." : "Log in to see your saved recipes."}</p>
              <button className="login-submit-btn" style={{ marginTop: 16, maxWidth: 180 }} onClick={() => setLoginModalOpen(true)}>
                {language === "kr" ? "로그인하기" : "Log in"}
              </button>
            </div>
          ) : savedRecipes.length === 0 ? (
            <div className="saved-tab-empty">
              <FaRegBookmark size={32} style={{ marginBottom: 12, opacity: 0.3 }} />
              <p>{language === "kr" ? "저장된 레시피가 없습니다." : "No saved recipes yet."}</p>
              <p style={{ fontSize: "0.85rem", opacity: 0.5 }}>
                {language === "kr" ? "레시피 카드의 북마크 버튼을 눌러 저장해보세요." : "Click the bookmark icon on any recipe card to save it."}
              </p>
            </div>
          ) : (
            <ul className="menu-list grid-list">
              {validRecipes
                .filter(item => savedRecipes.includes(item.id) && !deletedKeys.has(item.id))
                .map((item) => (
                  <div key={item.id} className="bookmark-card-wrap">
                    <RecipeCard {...makeCardProps(item)} />
                    {bookmarkNotes[item.id] && (
                      <div className={`bookmark-note-badge${darkMode ? ' dark' : ''}`}>📝</div>
                    )}
                  </div>
                ))}
            </ul>
          )}
        </div>
      ) : (
        <>
          {/* Hero Section */}
          <section className="hero">
            {chefProfile ? (
              <div className="chef-hero-identity">
                {channelProfiles[CHEF_FILTER] && (
                  <div className="chef-hero-avatar-ring">
                    <img
                      src={channelProfiles[CHEF_FILTER]}
                      alt={chefProfile.displayName}
                      className="chef-hero-avatar"
                    />
                  </div>
                )}
                <p className="chef-hero-name">{chefProfile.displayName}</p>
                <span className="chef-hero-count">{recipeCount}+ 레시피</span>
              </div>
            ) : (
              <div className="hero-badge">{t.heroBadge(recipeCount)}</div>
            )}
            <h1 className="hero-title">
              {(chefProfile
                ? (language === "kr" ? chefProfile.heroTitle : chefProfile.heroTitleEn)
                : t.heroTitle
              ).split("\n").map((line, i) => (
                <span key={i}>{line}{i === 0 && <br />}</span>
              ))}
            </h1>
            <p className="hero-subtitle">
              {chefProfile
                ? (language === "kr" ? chefProfile.heroSubtitle : chefProfile.heroSubtitleEn)
                : t.heroSubtitle}
            </p>
            <div className="hero-search">
              <TagSearch
                onSearch={handleSearch}
                options={availableGroupedOptions}
                language={language}
                darkMode={darkMode}
                value={selectedIngredients}
                onChange={setSelectedIngredients}
                onInputChange={handleSearchInputChange}
              />
            </div>

            {/* 인기 재료 태그 */}
            {!searchActive && !searchInputValue.trim() && (
              <div className="hero-popular">
                <span className="hero-popular-label">
                  {language === "kr" ? "인기" : "Popular"}
                </span>
                {popularTags.map((tag) => {
                  const opt = { value: tag, label: tag, group: "ingredient" };
                  const isSelected = selectedIngredients.some(s => s.value === tag);
                  return (
                    <button
                      key={tag}
                      className={`hero-popular-tag${isSelected ? " selected" : ""}`}
                      onClick={() => {
                        if (isSelected) return;
                        const next = [...selectedIngredients, opt];
                        setSelectedIngredients(next);
                        handleSearch(next);
                      }}
                    >
                      {tag}
                    </button>
                  );
                })}
              </div>
            )}


            {/* 검색 결과 수 */}
            {searchActive && (
              <div className="hero-result-msg">
                {language === "kr"
                  ? `${selectedIngredients.map(s => s.label).join(" + ")}(으)로 만들 수 있는 요리 ${sortedResults.length}가지`
                  : `${sortedResults.length} recipe${sortedResults.length !== 1 ? "s" : ""} with ${selectedIngredients.map(s => s.label).join(" + ")}`}
              </div>
            )}
            {!searchActive && debouncedInput.trim() && liveFilteredData && (
              <div className="hero-result-msg">
                {language === "kr"
                  ? `"${debouncedInput}" 관련 요리 ${sortedResults.length}가지`
                  : `${sortedResults.length} recipe${sortedResults.length !== 1 ? "s" : ""} for "${debouncedInput}"`}
              </div>
            )}

          </section>

          {/* Creator Tabs — 크리에이터 페이지에서만 표시 */}
          {chefProfile && !searchActive && (
            <div style={{ padding: '0 16px 0', maxWidth: 960, margin: '0 auto 0' }}>
              <div style={{
                display: 'flex', gap: 4, borderBottom: `2px solid ${darkMode ? '#333' : '#eee'}`,
                overflowX: 'auto', paddingBottom: 0, justifyContent: 'center',
              }}>
                {[
                  { key: 'all',      label: '모든 레시피' },
                  { key: 'featured', label: '대표메뉴' },
                  { key: 'recent',   label: '최신' },
                  { key: 'popular',  label: '인기' },
                  { key: 'triedit',  label: '따라해봤어요' },
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setCreatorTab(tab.key)}
                    style={{
                      padding: '10px 16px',
                      border: 'none', background: 'transparent',
                      fontSize: 14, fontWeight: creatorTab === tab.key ? 700 : 400,
                      color: creatorTab === tab.key ? '#3182f6' : (darkMode ? '#aaa' : '#666'),
                      borderBottom: creatorTab === tab.key ? '2px solid #3182f6' : '2px solid transparent',
                      marginBottom: -2, cursor: 'pointer', whiteSpace: 'nowrap',
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* All Menu */}
          <section className="section">
            <div className="container">
              <div className="section-header">
                <div>
                  <h2 className="section-title">{t.allRecipes}</h2>
                  {searchActive && (
                    <p className="search-result-count">
                      {language === "kr"
                        ? `${sortedResults.length}개 레시피 발견`
                        : `${sortedResults.length} recipe${sortedResults.length !== 1 ? "s" : ""} found`}
                    </p>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {(() => {
                    const activeCount =
                      (!CHEF_FILTER ? selectedChefs.length : 0) +
                      selectedServings.length +
                      selectedFoodType.length +
                      selectedCookStyle.length +
                      selectedSituation.length;
                    return (
                      <button
                        type="button"
                        onClick={() => setFilterDrawerOpen(true)}
                        className={`filter-trigger${activeCount > 0 ? " has-active" : ""}`}
                        aria-label={language === "kr" ? "필터 열기" : "Open filters"}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                        </svg>
                        <span>{language === "kr" ? "필터" : "Filters"}</span>
                        {activeCount > 0 && <span className="filter-trigger-badge">{activeCount}</span>}
                      </button>
                    );
                  })()}
                  <div className="sort-toggle">
                    <button
                      className={`sort-btn ${allMenuSort === "name" ? "active" : ""}`}
                      onClick={() => setAllMenuSort("name")}
                    >
                      {t.nameSort}
                    </button>
                    <button
                      className={`sort-btn ${allMenuSort === "date" ? "active" : ""}`}
                      onClick={() => setAllMenuSort("date")}
                    >
                      {t.dateSort}
                    </button>
                  </div>
                </div>
              </div>
              <ul className="menu-list grid-list">
                {recipeLoading && displayRecipes.length === 0 ? (
                  Array.from({ length: 12 }).map((_, i) => (
                    <li key={i} className="skeleton-card" style={{ animationDelay: `${i * 0.05}s` }} />
                  ))
                ) : displayRecipes.length > 0 ? (
                  displayRecipes.map((item, i) => (
                    <RecipeCard key={item.id || item.url} {...makeCardProps(item)} style={{ animationDelay: `${Math.min(i, 11) * 0.04}s` }} />
                  ))
                ) : (
                  <p className="no-results">{t.noResults}</p>
                )}
              </ul>
            </div>
          </section>

          {/* Footer */}
          <footer className={`site-footer${darkMode ? " dark" : ""}`}>
            <div className="site-footer-inner">
              <span className="site-footer-brand">Findish</span>
              <span className="site-footer-copy">© 2026 Findish. All rights reserved.</span>
              <div className="site-footer-links">
                <a href="https://www.instagram.com/andy__yeyo/" target="_blank" rel="noopener noreferrer">📸 andy_yeyo</a>
                <a href="mailto:ndk68790@gmail.com">✉️ ndk68790@gmail.com</a>
                {!chefProfile && <a href="/about" onClick={(e) => { e.preventDefault(); navigate('/about'); }}>About</a>}
                {!chefProfile && <a href="/creators" onClick={(e) => { e.preventDefault(); navigate('/creators'); }}>Creators</a>}
              </div>
            </div>
          </footer>

          {/* Filter Drawer */}
          {filterDrawerOpen && (
            <div
              className="filter-drawer-backdrop"
              onClick={() => setFilterDrawerOpen(false)}
            />
          )}
          <aside
            className={`filter-drawer${filterDrawerOpen ? " open" : ""}${darkMode ? " dark" : ""}`}
            aria-hidden={!filterDrawerOpen}
          >
            <div className="filter-drawer-header">
              <h3>{language === "kr" ? "필터" : "Filters"}</h3>
              <button
                type="button"
                className="filter-drawer-close"
                onClick={() => setFilterDrawerOpen(false)}
                aria-label={language === "kr" ? "닫기" : "Close"}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <div className="filter-drawer-body">
              {!CHEF_FILTER && (
                <div className="filter-group">
                  <div className="filter-group-label">{t.allChefs}</div>
                  <div className="filter-chips">
                    {chefOptions.map((chef) => {
                      const displayName = chefConfig[chef]?.displayName || chef;
                      const active = selectedChefs.includes(chef);
                      return (
                        <button
                          key={chef}
                          type="button"
                          className={`filter-chip${active ? " active" : ""}`}
                          onClick={() => toggleInArray(setSelectedChefs)(chef)}
                        >
                          {displayName}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {[
                { key: 'servings',  val: selectedServings,  set: setSelectedServings },
                { key: 'foodType',  val: selectedFoodType,  set: setSelectedFoodType },
                { key: 'cookStyle', val: selectedCookStyle, set: setSelectedCookStyle },
                { key: 'situation', val: selectedSituation, set: setSelectedSituation },
              ].map(({ key, val, set }) => (
                <div key={key} className="filter-group">
                  <div className="filter-group-label">{TAG_GROUPS[key].label}</div>
                  <div className="filter-chips">
                    {TAG_GROUPS[key].tags.map(tag => (
                      <button
                        key={tag}
                        type="button"
                        className={`filter-chip${val.includes(tag) ? " active" : ""}`}
                        onClick={() => toggleInArray(set)(tag)}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="filter-drawer-footer">
              <button
                type="button"
                className="filter-drawer-reset"
                onClick={() => {
                  setSelectedChefs([]);
                  setSelectedServings([]);
                  setSelectedFoodType([]);
                  setSelectedCookStyle([]);
                  setSelectedSituation([]);
                }}
              >
                {language === "kr" ? "초기화" : "Reset"}
              </button>
              <button
                type="button"
                className="filter-drawer-apply"
                onClick={() => setFilterDrawerOpen(false)}
              >
                {language === "kr" ? "결과 보기" : "See results"}
              </button>
            </div>
          </aside>
        </>
      )}

      {confirmDialog && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "rgba(0,0,0,0.45)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onClick={() => setConfirmDialog(null)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: darkMode ? "#1e1e1e" : "#fff",
              color: darkMode ? "#f0f0f0" : "#111",
              borderRadius: 16,
              padding: "28px 32px",
              minWidth: 280,
              maxWidth: 360,
              boxShadow: "0 8px 32px rgba(0,0,0,0.22)",
              textAlign: "center",
            }}
          >
            <p style={{ margin: "0 0 24px", fontSize: 15, lineHeight: 1.6 }}>
              {confirmDialog.message}
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button
                onClick={() => setConfirmDialog(null)}
                style={{
                  flex: 1, padding: "10px 0", borderRadius: 10, border: "none",
                  background: darkMode ? "#2a2a2a" : "#f0f0f0",
                  color: darkMode ? "#ccc" : "#555",
                  fontSize: 14, fontWeight: 600, cursor: "pointer",
                }}
              >
                취소
              </button>
              <button
                onClick={confirmDialog.onConfirm}
                style={{
                  flex: 1, padding: "10px 0", borderRadius: 10, border: "none",
                  background: "#e53e3e", color: "#fff",
                  fontSize: 14, fontWeight: 600, cursor: "pointer",
                }}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
